import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { leadsAPI, leadSettingsAPI, leadActivitiesAPI, hrAPI, inventoryAPI, normalizeList, normalizeError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, Search, Edit, Trash2, LayoutGrid, Table as TableIcon, Phone, Mail, FileText, DollarSign, ExternalLink, Building2, User, Filter, X, Settings, Upload, Download, Bot, Zap, Activity, Loader2, FileSpreadsheet, CheckCircle2, Check, AlertCircle, ChevronLeft, ChevronRight, GripVertical, Sparkles, Target, Lock, Unlock, Eye, RefreshCw, ShieldAlert, PhoneIncoming, UserX, UserPlus } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { handleError } from '../lib/errorHandler';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';
import { useWebSocket, EventTypes, Channels } from '../hooks/useWebSocket';
import * as XLSX from 'xlsx';
import { useCurrency } from '../contexts/CurrencyContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { KanbanSkeleton, TableSkeleton } from '../components/ui/loading-states';
import { EmptyState, SearchEmptyState } from '../components/ui/empty-states';
import { ErrorState } from '../components/ui/error-states';
import { DataPagination } from '../components/ui/data-pagination';
import { PageLoading, PageError, PageEmpty } from '../components/ui/page-guards';
import { Progress } from '../components/ui/progress';
import { Confetti } from '../components/ui/confetti';
import { LeadFormSheet } from '../components/LeadFormSheet';
import { LeadKanbanCard } from '../components/LeadKanbanCard';
import { EmailDialog, NoteDialog, CallDialog, SummaryDialog } from '../components/LeadQuickActions';
import { LeadImportSheet } from '../components/LeadImportSheet';
import { LeadPreviewPanel } from '../components/LeadPreviewPanel';
import LeadKanbanView from '../components/leads/LeadKanbanView';
import { LeadTableView } from '../components/leads/LeadTableView';
import { LeadFiltersDialog } from '../components/leads/LeadFilters';
import { LeadDiscoverDialog, BulkRevealDialog, BulkIcpRevealDialog } from '../components/leads/LeadDiscoverDialog';
import { LeadToolbar } from '../components/leads/LeadToolbar';
import { LeadOnboardingOverlay } from '../components/leads/LeadOnboardingOverlay';
import { BackfillBanner } from '../components/leads/BackfillBanner';
import EnrollmentBadge from '../components/leads/EnrollmentBadge';
import EmailCleanupBadge from '../components/leads/EmailCleanupBadge';
import BatchSelectMenu from '../components/leads/BatchSelectMenu';
// OnboardingWowModal removed

// --- Lead import: parse-time sanitization & header mapping (Phase 1 hardening) ---
// Robust money parser: strips currency symbols / thousands separators / stray text
// and NEVER returns NaN (NaN → JSON null → silent data issues downstream).
const parseImportMoney = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// Accept common header variants so real-world spreadsheets map correctly instead
// of silently dropping columns (headers are matched trimmed + case-insensitive).
const IMPORT_HEADER_ALIASES = {
  name: ['name', 'full name', 'fullname', 'lead name', 'contact name', 'contact', 'first name'],
  email: ['email', 'email address', 'e-mail', 'mail', 'email id'],
  phone: ['phone', 'phone number', 'phone no', 'phone no.', 'phone_number', 'mobile', 'mobile number', 'mobile no', 'mobile no.', 'mobile_number', 'contact number', 'contact no', 'contact no.', 'contact_number', 'whatsapp', 'whatsapp number', 'whatsapp no', 'whatsapp_number', 'cell', 'cell phone', 'cellphone', 'telephone', 'tel'],
  company: ['company', 'company name', 'organization', 'organisation', 'org', 'business', 'account'],
  status: ['status', 'stage', 'pipeline stage', 'lead status'],
  source: ['source', 'lead source', 'channel'],
  value: ['value', 'deal value', 'amount', 'revenue', 'potential value', 'deal size'],
  notes: ['notes', 'note', 'comments', 'remarks', 'description'],
  address: ['address', 'street', 'street address'],
  city: ['city', 'town'],
  state: ['state', 'province', 'region'],
  zip_code: ['zip_code', 'zip', 'zip code', 'postal code', 'postcode', 'pincode', 'pin code'],
  product: ['product', 'service', 'product/service', 'product interest', 'service interest', 'offering', 'interested in', 'item', 'plan', 'package'],
};

const getImportField = (normRow, field) => {
  for (const alias of (IMPORT_HEADER_ALIASES[field] || [])) {
    const val = normRow[alias];
    if (val !== null && val !== undefined && String(val).trim() !== '') return String(val).trim();
  }
  return '';
};

const isValidEmail = (e) => !e || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

// Normalize a raw lead `status` (which has drifted: "new"/"New"/"New Lead") to a
// canonical pipeline stage — mirrors the backend `status_to_pipeline_stage` so the
// List-view status filter matches leads regardless of casing/legacy drift.
const STATUS_STAGE_MAP = {
  'new': 'New Lead', 'new lead': 'New Lead', 'contacted': 'Contacted',
  'qualified': 'Qualified', 'proposal': 'Proposal/Quote Sent',
  'proposal/quote sent': 'Proposal/Quote Sent', 'negotiation': 'Negotiation',
  'won': 'Won', 'converted': 'Won', 'lost': 'Lost', 'disqualified': 'Lost',
};
const toStage = (s) => {
  const k = (s || '').trim().toLowerCase();
  return STATUS_STAGE_MAP[k] || (s || '').trim();
};

// Default fallback pipeline stages for empty state display
const DEFAULT_PIPELINE_STAGES = [
  { id: 'default-1', name: 'New Lead', color: '#3b82f6', order: 1 },
  { id: 'default-2', name: 'Contacted', color: '#8b5cf6', order: 2 },
  { id: 'default-3', name: 'Qualified', color: '#06b6d4', order: 3 },
  { id: 'default-4', name: 'Proposal Sent', color: '#f59e0b', order: 4 },
  { id: 'default-5', name: 'Negotiation', color: '#f97316', order: 5 },
  { id: 'default-6', name: 'Won', color: '#10b981', order: 6 },
  { id: 'default-7', name: 'Lost', color: '#ef4444', order: 7 },
];

const statusConfigs = {
  new: { 
    label: 'New', 
    color: 'hsl(0 0% 50%)',
    bgColor: 'hsl(0 0% 12%)',
    textColor: 'hsl(0 0% 80%)'
  },
  contacted: { 
    label: 'Contacted', 
    color: 'hsl(0 0% 50%)',
    bgColor: 'hsl(0 0% 12%)',
    textColor: 'hsl(0 0% 80%)'
  },
  qualified: { 
    label: 'Qualified', 
    color: 'hsl(0 0% 50%)',
    bgColor: 'hsl(0 0% 12%)',
    textColor: 'hsl(0 0% 80%)'
  },
  converted: { 
    label: 'Converted', 
    color: 'hsl(0 0% 50%)',
    bgColor: 'hsl(0 0% 12%)',
    textColor: 'hsl(0 0% 80%)'
  },
  lost: { 
    label: 'Lost', 
    color: 'hsl(0 0% 50%)',
    bgColor: 'hsl(0 0% 12%)',
    textColor: 'hsl(0 0% 80%)'
  }
};

export default function LeadsKanban() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currency = useCurrency();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  // Unassigned chip: any admin/owner/manager on ANY plan (standard CRM owner filter).
  // Best practice = availability by ROLE, and auto-hide by DATA (see unassignedCount)
  // rather than gating by plan.
  const canSeeUnassigned = ['superadmin', 'admin', 'owner', 'workspace admin', 'manager', 'sales_manager'].includes((user?.role || '').toLowerCase());
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [leads, setLeads] = useState([]);
  // Iter 61 — banner-click filter: show only Bronze/needs-review leads. Toggled
  // by clicking the amber Bronze banner; clearable via the chip below it.
  const [bronzeOnly, setBronzeOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slowLoad, setSlowLoad] = useState(false); // true if first load exceeds 8s → show retry escape hatch
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const hasLoadedOnceRef = useRef(false);
  const [totalLeadCount, setTotalLeadCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLead, setPreviewLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null); // Track which column is being dragged over
  const [view, setView] = useState('kanban'); // 'kanban' or 'table'
  const [teamMembers, setTeamMembers] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);  // Changed from leadStatuses to pipelineStages for Kanban
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [importSheetOpen, setImportSheetOpen] = useState(false);
  // Auto-open the CSV importer when arriving from Audience (/leads?import=1).
  useEffect(() => {
    if (searchParams.get('import') === '1') {
      setImportSheetOpen(true);
      searchParams.delete('import');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [visibleStatuses, setVisibleStatuses] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  
  // Kanban server-side pagination state
  const [kanbanData, setKanbanData] = useState(null); // { columns: {stage: {leads, total, has_more}}, stages: [] }
  const [loadingMoreColumn, setLoadingMoreColumn] = useState(null);

  // Perf guards (Iter: pipeline WS-storm fix) — during live autonomy the brain emits
  // a continuous stream of LEAD_SCORED/UPDATED events. Without these, every event
  // re-fetched the heavy /api/leads/kanban aggregation, overlapping calls piled up
  // and eventually tripped the 30s API timeout, and constant kanbanData replacement
  // made manual edits feel laggy. We (a) dedupe in-flight kanban loads, (b) coalesce
  // WS-triggered reloads to at most one per few seconds, (c) never reload mid-drag.
  const loadInFlightRef = useRef(false);
  const pendingReloadRef = useRef(false);
  const loadLeadsRef = useRef(null);
  const draggedLeadRef = useRef(null);
  const wsReloadScheduledRef = useRef(false);
  
  // Onboarding flow state
  const [onboardingStep, setOnboardingStep] = useState(null); // 'highlight' | null
  const [highlightedLeadId, setHighlightedLeadId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const highlightedCardRef = useRef(null);
  
  // Onboarding discovery state — shown when AI is generating leads in background
  const [isDiscoveringLeads, setIsDiscoveringLeads] = useState(false);
  const discoveryPollRef = useRef(null);
  const discoveryCheckedRef = useRef(false);
  
  // AI call polling state
  const [activeCallId, setActiveCallId] = useState(null);
  const [activeCallLeadId, setActiveCallLeadId] = useState(null);
  const [callPolling, setCallPolling] = useState(false);
  const pollIntervalRef = useRef(null);
  
  // Summary dialog state
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryDialogData, setSummaryDialogData] = useState(null);
  const [leadSummaries, setLeadSummaries] = useState({}); // { leadId: [summaries] }

  // Debounce search to prevent reload on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importStatus, setImportStatus] = useState('idle'); // idle, parsing, preview, importing, complete, error
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState(null); // {total,new_count,duplicate_count,duplicates}
  const [duplicateStrategy, setDuplicateStrategy] = useState('update'); // 'update' | 'skip'
  const [skipAiScoring, setSkipAiScoring] = useState(false); // Phase 0 — import without AI scoring
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateCheckFailed, setDuplicateCheckFailed] = useState(false);
  const fileInputRef = useRef(null);
  
  // Kanban scroll state
  const kanbanScrollRef = useRef(null);
  // Continuous edge auto-scroll state for Kanban drag (rAF-driven so it keeps
  // scrolling even when the pointer is held still at the edge).
  const kanbanAutoScrollRef = useRef({ dir: 0, speed: 0, raf: null });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    dateFrom: '',
    dateTo: '',
    assignedTo: '',
    icp: '',
    product: '',
    createdBy: ''
  });

  // Load catalog products/services for the Product filter (G5).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await inventoryAPI.getItems({ limit: 500 });
        const list = normalizeList(res.data, 'items') || res.data?.data || [];
        if (alive) setProducts((Array.isArray(list) ? list : []).map(p => ({ id: p.id, name: p.name })).filter(p => p.id && p.name));
      } catch { /* non-fatal — filter just shows no products */ }
    })();
    return () => { alive = false; };
  }, []);
  
  // Pagination state (for table view)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Quick action dialogs state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [quickActionLead, setQuickActionLead] = useState(null);
  
  // AI Actions state
  const [aiStatus, setAiStatus] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiActivities, setAiActivities] = useState([]);
  
  // Reveal state
  const [revealingLeadId, setRevealingLeadId] = useState(null);
  const [revealCredits, setRevealCredits] = useState(null);
  const [bulkRevealColumn, setBulkRevealColumn] = useState(null);
  const [bulkRevealConfirm, setBulkRevealConfirm] = useState(null);
  const [bulkIcpRevealOpen, setBulkIcpRevealOpen] = useState(false);
  const [bulkIcpRevealing, setBulkIcpRevealing] = useState(false);
  const [icpQuality, setIcpQuality] = useState(null); // {method, apollo_enriched}
  
  // Discover state
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredContacts, setDiscoveredContacts] = useState([]);
  const [selectedDiscovered, setSelectedDiscovered] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportLeads = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.status) params.status = filters.status;
      if (filters.assignedTo) params.assigned_to = filters.assignedTo;
      if (filters.source) params.source = filters.source;
      if (filters.icp) params.icp = filters.icp;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.product) params.product = filters.product;
      if (filters.createdBy) params.created_by = filters.createdBy;
      const res = await leadsAPI.exportCsv(params);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Pipeline exported to CSV');
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('CSV export is available on paid plans. Upgrade to export your pipeline.');
      } else {
        toast.error('Export failed. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  }, [debouncedSearch, filters]);
  
  // Quick action form data
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [noteForm, setNoteForm] = useState({ text: '' });
  const [callForm, setCallForm] = useState({ duration: '', notes: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    status: 'New Lead',
    source: '',
    value: 0,
    notes: '',
    assigned_to: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    interested_product_id: '',
  });

  // loadLeads must be defined as useCallback BEFORE startCallPolling
  const loadLeads = useCallback(async () => {
    // Dedupe: never run two kanban loads at once. A request arriving while one is
    // in flight just marks a single trailing reload (collapses bursts → no pile-up
    // of slow aggregations that would otherwise hit the 30s timeout).
    if (loadInFlightRef.current) { pendingReloadRef.current = true; return; }
    loadInFlightRef.current = true;
    try {
      // Only show full-page loading on very first load (no data yet)
      if (!hasLoadedOnceRef.current) {
        setLoading(true);
      }
      setError(null);
      
      // Both views use server-side search
      const params = { search: debouncedSearch };
      
      if (view === 'table') {
        const skip = (currentPage - 1) * pageSize;
        params.limit = pageSize;
        params.skip = skip;
        // Push the status filter to the server so it matches ALL leads across
        // pages (and tolerates the "new"/"New"/"New Lead" drift server-side),
        // not just the current page.
        if (filters.status) params.status = filters.status;
        // Push assigned_to to the server so the filter (incl. the "Unassigned" chip)
        // matches ALL leads across pages, not just whichever page is currently loaded.
        if (filters.assignedTo) params.assigned_to = filters.assignedTo;
        // G4/G5 — push the rest server-side so list & kanban behave identically.
        if (filters.source) params.source = filters.source;
        if (filters.icp) params.icp = filters.icp;
        if (filters.dateFrom) params.date_from = filters.dateFrom;
        if (filters.dateTo) params.date_to = filters.dateTo;
        if (filters.product) params.product = filters.product;
        if (filters.createdBy) params.created_by = filters.createdBy;

        // Fetch leads + total count (scoped to the same filters) in parallel
        const countParams = {};
        if (filters.status) countParams.status = filters.status;
        if (filters.assignedTo) countParams.assigned_to = filters.assignedTo;
        if (filters.source) countParams.source = filters.source;
        if (filters.icp) countParams.icp = filters.icp;
        if (filters.dateFrom) countParams.date_from = filters.dateFrom;
        if (filters.dateTo) countParams.date_to = filters.dateTo;
        if (filters.product) countParams.product = filters.product;
        if (filters.createdBy) countParams.created_by = filters.createdBy;
        const [leadsRes, totalCountRes] = await Promise.all([
          leadsAPI.getAll(params),
          leadsAPI.getCount(countParams)
        ]);
        
        const leadsData = normalizeList(leadsRes.data, 'leads');
        setLeads(leadsData);
        setTotalLeadCount(totalCountRes.data?.count || leadsData.length);
        setTotalCount(totalCountRes.data?.count || leadsData.length);
      } else {
        // Kanban view: use server-side per-column pagination
        const kanbanParams = { per_column: 20 };
        if (debouncedSearch) kanbanParams.search = debouncedSearch;
        if (filters.assignedTo) kanbanParams.assigned_to = filters.assignedTo;
        if (filters.icp) kanbanParams.icp = filters.icp;
        if (filters.source) kanbanParams.source = filters.source;
        if (filters.dateFrom) kanbanParams.date_from = filters.dateFrom;
        if (filters.dateTo) kanbanParams.date_to = filters.dateTo;
        if (filters.product) kanbanParams.product = filters.product;
        if (filters.createdBy) kanbanParams.created_by = filters.createdBy;
        
        const kanbanCountParams = {};
        if (filters.assignedTo) kanbanCountParams.assigned_to = filters.assignedTo;
        if (filters.icp) kanbanCountParams.icp = filters.icp;
        if (filters.source) kanbanCountParams.source = filters.source;
        if (filters.dateFrom) kanbanCountParams.date_from = filters.dateFrom;
        if (filters.dateTo) kanbanCountParams.date_to = filters.dateTo;
        if (filters.product) kanbanCountParams.product = filters.product;
        if (filters.createdBy) kanbanCountParams.created_by = filters.createdBy;
        const [kanbanRes, totalCountRes] = await Promise.all([
          leadsAPI.getKanban(kanbanParams),
          leadsAPI.getCount(kanbanCountParams)
        ]);
        
        const data = kanbanRes.data;
        setKanbanData(data);
        setTotalLeadCount(totalCountRes.data?.count || 0);
        
        // Flatten all column leads into the leads array for compatibility
        // (filters, preview panel, etc. may reference it)
        const allLeads = [];
        for (const col of Object.values(data.columns || {})) {
          allLeads.push(...(col.leads || []));
        }
        setLeads(allLeads);
      }
      
      hasLoadedOnceRef.current = true;
    } catch (err) {
      handleError('Leads', err, { fallbackMessage: 'Failed to load leads' });
      setError(normalizeError(err, 'Failed to load leads'));
      toast.error(normalizeError(err, 'Failed to load leads'));
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
      // Run exactly one trailing reload if events arrived mid-flight.
      if (pendingReloadRef.current) {
        pendingReloadRef.current = false;
        setTimeout(() => { loadLeadsRef.current && loadLeadsRef.current(); }, 0);
      }
    }
  }, [debouncedSearch, view, currentPage, pageSize, filters.status, filters.assignedTo, filters.icp, filters.source, filters.dateFrom, filters.dateTo, filters.product, filters.createdBy]);

  // Load more leads for a specific Kanban column
  const loadMoreColumn = useCallback(async (stage) => {
    if (!kanbanData || loadingMoreColumn) return;
    const col = kanbanData.columns?.[stage];
    if (!col || !col.has_more) return;

    setLoadingMoreColumn(stage);
    try {
      const params = { skip: col.leads.length, limit: 20 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.assignedTo) params.assigned_to = filters.assignedTo;
      if (filters.icp) params.icp = filters.icp;
      if (filters.source) params.source = filters.source;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.product) params.product = filters.product;
      if (filters.createdBy) params.created_by = filters.createdBy;

      const res = await leadsAPI.getKanbanColumn(stage, params);
      const moreLeads = res.data?.leads || [];

      setKanbanData(prev => {
        if (!prev) return prev;
        const updated = { ...prev, columns: { ...prev.columns } };
        const existing = updated.columns[stage];
        updated.columns[stage] = {
          ...existing,
          leads: [...existing.leads, ...moreLeads],
          has_more: existing.leads.length + moreLeads.length < existing.total,
        };
        return updated;
      });

      // Also update flat leads array for compatibility
      setLeads(prev => [...prev, ...moreLeads]);
    } catch (err) {
      toast.error('Failed to load more leads');
    } finally {
      setLoadingMoreColumn(null);
    }
  }, [kanbanData, loadingMoreColumn, debouncedSearch, filters.assignedTo, filters.icp, filters.source, filters.dateFrom, filters.dateTo, filters.product, filters.createdBy]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Live count of owner-less leads → powers the "Unassigned N" chip and auto-hides
  // it when everything already has an owner. Refreshes whenever the lead set changes.
  useEffect(() => {
    if (!canSeeUnassigned) return;
    let cancelled = false;
    leadsAPI.getCount({ assigned_to: 'unassigned' })
      .then((r) => { if (!cancelled) setUnassignedCount(r.data?.count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canSeeUnassigned, totalLeadCount]);


  // Escape hatch: if the very first load stalls (real backend slowdown), surface a
  // manual Retry after 8s so the pipeline never *looks* frozen on the skeleton.
  useEffect(() => {
    if (!(loading && !hasLoadedOnceRef.current)) { setSlowLoad(false); return; }
    const t = setTimeout(() => setSlowLoad(true), 8000);
    return () => clearTimeout(t);
  }, [loading]);

  // Keep refs pointing at the latest values so the (stable) WS handler never
  // re-subscribes on every render and always calls the current loader.
  useEffect(() => { loadLeadsRef.current = loadLeads; }, [loadLeads]);
  useEffect(() => { draggedLeadRef.current = draggedLead; }, [draggedLead]);

  // Phase P3: Real-time Kanban — when any teammate (or the autonomous brain) updates
  // a lead, refresh silently. COALESCED: during live autonomy the brain streams
  // LEAD_SCORED/UPDATED events continuously; we cap reloads to at most one per
  // WS_REFRESH_WINDOW_MS and skip while the user is mid-drag (so the edit isn't
  // clobbered and slow kanban aggregations don't pile up into 30s timeouts).
  const WS_REFRESH_WINDOW_MS = 4000;
  const wsRefreshTimerRef = useRef(null);
  const scheduleWsRefresh = useCallback(() => {
    if (wsReloadScheduledRef.current) return; // a reload is already queued — coalesce
    wsReloadScheduledRef.current = true;
    const fire = () => {
      if (draggedLeadRef.current) {
        // User is mid-drag — defer briefly so the optimistic move isn't overwritten.
        wsRefreshTimerRef.current = setTimeout(fire, 1500);
        return;
      }
      wsReloadScheduledRef.current = false;
      loadLeadsRef.current && loadLeadsRef.current();
    };
    wsRefreshTimerRef.current = setTimeout(fire, WS_REFRESH_WINDOW_MS);
  }, []);
  useEffect(() => () => { if (wsRefreshTimerRef.current) clearTimeout(wsRefreshTimerRef.current); }, []);
  // ─── Phase P4: Granular Kanban patching (feature-flagged) ──────────────────
  // Instead of re-fetching the whole board on every brain/cadence event, patch the
  // single changed lead in place. A reconcile (full reload on reconnect + tab focus)
  // is kept as a safety net so the board can never silently drift from the DB.
  // Disable instantly with REACT_APP_KANBAN_GRANULAR=false (falls back to full reload).
  const KANBAN_GRANULAR = process.env.REACT_APP_KANBAN_GRANULAR !== 'false';
  const pendingPatchIdsRef = useRef(new Set());
  const patchTimerRef = useRef(null);
  const filtersActiveRef = useRef(false);
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => {
    filtersActiveRef.current = !!(debouncedSearch || filters.assignedTo || filters.icp || bronzeOnly);
  }, [debouncedSearch, filters.assignedTo, filters.icp, bronzeOnly]);

  const colKeyForStage = (columns, stage) =>
    Object.keys(columns).find(s => s.toLowerCase() === String(stage || '').toLowerCase());

  // Patch a single lead into the board (fetch fresh truth, then place/move/remove).
  const applyOneLeadPatch = useCallback(async (leadId) => {
    if (!leadId) return;
    if (draggedLeadRef.current && draggedLeadRef.current.id === leadId) return; // don't fight a live drag
    let lead = null;
    try {
      const res = await leadsAPI.getOne(leadId);
      lead = res.data?.lead || res.data;
    } catch (err) {
      if (err?.response?.status === 404) {
        // Lead gone (deleted/converted) → remove it from the board.
        setKanbanData(prev => {
          if (!prev?.columns) return prev;
          const cols = { ...prev.columns };
          let changed = false;
          for (const k of Object.keys(cols)) {
            if ((cols[k].leads || []).some(l => l.id === leadId)) {
              cols[k] = { ...cols[k], leads: cols[k].leads.filter(l => l.id !== leadId),
                          total: Math.max(0, (cols[k].total || 1) - 1) };
              changed = true;
            }
          }
          return changed ? { ...prev, columns: cols } : prev;
        });
      }
      return;
    }
    if (!lead || !lead.id) return;
    const targetStage = lead.pipeline_stage || lead.status;
    setKanbanData(prev => {
      if (!prev?.columns) return prev;
      const cols = { ...prev.columns };
      let fromKey = null;
      for (const k of Object.keys(cols)) {
        if ((cols[k].leads || []).some(l => l.id === lead.id)) { fromKey = k; break; }
      }
      const toKey = colKeyForStage(cols, targetStage);

      // Version guard — never let an older snapshot overwrite a newer card.
      if (fromKey) {
        const existing = (cols[fromKey].leads || []).find(l => l.id === lead.id);
        if (existing?.updated_at && lead.updated_at &&
            new Date(existing.updated_at) > new Date(lead.updated_at)) {
          return prev;
        }
      }

      if (!fromKey) {
        // Not on board. With an active filter/search we can't be sure it belongs in
        // the current view → defer to a reconcile rather than insert blindly.
        if (filtersActiveRef.current) { scheduleWsRefresh(); return prev; }
        if (!toKey) return prev; // its stage isn't a visible column (won/lost/etc.)
        cols[toKey] = { ...cols[toKey], leads: [lead, ...(cols[toKey].leads || [])],
                        total: (cols[toKey].total || 0) + 1 };
        return { ...prev, columns: cols };
      }
      if (!toKey) {
        // Stage no longer maps to a visible column → remove from board.
        cols[fromKey] = { ...cols[fromKey], leads: cols[fromKey].leads.filter(l => l.id !== lead.id),
                          total: Math.max(0, (cols[fromKey].total || 1) - 1) };
        return { ...prev, columns: cols };
      }
      if (fromKey === toKey) {
        cols[fromKey] = { ...cols[fromKey],
          leads: cols[fromKey].leads.map(l => l.id === lead.id ? { ...l, ...lead } : l) };
        return { ...prev, columns: cols };
      }
      // Moved between columns.
      cols[fromKey] = { ...cols[fromKey], leads: cols[fromKey].leads.filter(l => l.id !== lead.id),
                        total: Math.max(0, (cols[fromKey].total || 1) - 1) };
      cols[toKey] = { ...cols[toKey],
                      leads: [lead, ...cols[toKey].leads.filter(l => l.id !== lead.id)],
                      total: (cols[toKey].total || 0) + 1 };
      return { ...prev, columns: cols };
    });
  }, [scheduleWsRefresh]);

  // Micro-batch: collect lead ids over a 300ms window and apply once (one render burst).
  // BURST GUARD (P0 — first-visit pipeline hang): each patch fires its own getOne
  // request. During first-time brain seeding / bulk imports the brain emits dozens
  // of LEAD_SCORED/UPDATED events in a burst; firing one getOne per lead saturates
  // the browser's ~6 concurrent-connection pool and starves the initial /kanban load,
  // so the board appeared to hang on the very first visit (then worked once seeding
  // finished and events went sparse). Collapse any burst into a single cheap
  // full-board reload instead of N individual fetches.
  const BURST_PATCH_THRESHOLD = 6;
  const flushPatches = useCallback(() => {
    if (draggedLeadRef.current) { patchTimerRef.current = setTimeout(flushPatches, 800); return; }
    const ids = Array.from(pendingPatchIdsRef.current);
    pendingPatchIdsRef.current = new Set();
    if (ids.length > BURST_PATCH_THRESHOLD) { scheduleWsRefresh(); return; }
    ids.forEach(id => applyOneLeadPatch(id));
  }, [applyOneLeadPatch, scheduleWsRefresh]);

  const onLeadEvent = useCallback((data) => {
    // Until the board's first load completes, ignore live events entirely — the
    // initial loadLeads() already fetches the freshest snapshot, and firing getOne
    // patches now would only compete with (and slow down) that first paint.
    if (!hasLoadedOnceRef.current) return;
    // Table view or flag off → fall back to the proven full reload.
    if (!KANBAN_GRANULAR || viewRef.current !== 'kanban') { scheduleWsRefresh(); return; }
    const leadId = data?.lead_id || data?.id;
    if (!leadId) { scheduleWsRefresh(); return; }
    pendingPatchIdsRef.current.add(leadId);
    if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
    patchTimerRef.current = setTimeout(flushPatches, 300);
  }, [KANBAN_GRANULAR, flushPatches, scheduleWsRefresh]);

  useEffect(() => () => { if (patchTimerRef.current) clearTimeout(patchTimerRef.current); }, []);

  // Reconcile safety net #1: refresh when the tab regains focus (catches any events
  // missed while backgrounded). Coalesced via scheduleWsRefresh.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') scheduleWsRefresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [scheduleWsRefresh]);

  const { isConnected: wsConnected } = useWebSocket(
    [Channels.SALES],
    {
      [EventTypes.LEAD_UPDATED]: onLeadEvent,
      [EventTypes.LEAD_CREATED]: onLeadEvent,
      [EventTypes.LEAD_SCORED]: onLeadEvent,
    },
    { showToasts: false, autoConnect: true }
  );

  // Reconcile safety net #2: on (re)connect, rebuild a clean baseline so missed
  // messages during a disconnect can't leave the board drifted.
  const prevConnectedRef = useRef(false);
  useEffect(() => {
    if (wsConnected && !prevConnectedRef.current && hasLoadedOnceRef.current) {
      loadLeadsRef.current && loadLeadsRef.current();
    }
    prevConnectedRef.current = wsConnected;
  }, [wsConnected]);

  // Check if onboarding lead generation is in progress when leads are empty
  useEffect(() => {
    if (loading || leads.length > 0 || discoveryCheckedRef.current) return;
    
    const checkOnboardingDiscovery = async () => {
      try {
        const res = await api.get('/api/onboarding/status');
        const data = res.data;
        if (data.onboarding_completed && !data.has_created_lead && data.brain_data_seeded === false) {
          setIsDiscoveringLeads(true);
        }
      } catch (err) {
        console.error('[Leads] Failed to check onboarding status:', err);
      }
      discoveryCheckedRef.current = true;
    };
    
    checkOnboardingDiscovery();
  }, [loading, leads.length]);

  // Auto-poll for leads when onboarding discovery is in progress
  useEffect(() => {
    if (!isDiscoveringLeads) return;
    
    discoveryPollRef.current = setInterval(async () => {
      try {
        const response = await leadsAPI.getAll({ limit: 1 });
        const leadsData = normalizeList(response.data, 'leads');
        if (leadsData.length > 0) {
          setIsDiscoveringLeads(false);
          setShowConfetti(true);
          loadLeads();
        }
      } catch (err) {
        // Silently retry
      }
    }, 5000);
    
    return () => {
      if (discoveryPollRef.current) clearInterval(discoveryPollRef.current);
    };
  }, [isDiscoveringLeads, loadLeads]);

  // Load AI call summaries lazily - only on demand when a lead card is expanded
  // Removed the N+1 batch loading that fired 50 requests on mount

  useEffect(() => {
    // Phase 2D (Iter 59) — Single bootstrap call replaces 5 separate GETs.
    // Falls back to individual endpoints on bootstrap failure so the page
    // still hydrates if /_bootstrap is ever unavailable.
    let cancelled = false;

    const hydrateFromBootstrap = (data) => {
      if (cancelled || !data) return;

      // Team members (with current user appended if missing)
      const team = (data.team_members || []).map(u => ({ id: u.id, name: u.name, email: u.email }));
      if (user && !team.find(m => m.id === user.id)) {
        team.push({ id: user.id, name: user.name, email: user.email });
      }
      setTeamMembers(team);

      // Lead sources
      setLeadSources(Array.isArray(data.lead_sources) ? data.lead_sources : []);

      // Pipeline stages — keep DEFAULT fallback if backend returns empty
      const stages = Array.isArray(data.pipeline_stages) ? data.pipeline_stages : [];
      const sortedStages = stages.sort((a, b) => (a.order || 0) - (b.order || 0));
      if (sortedStages.length > 0) {
        setPipelineStages(sortedStages);
        setVisibleStatuses(prev => prev.length > 0 ? prev : sortedStages.filter(s => s.visible !== false).map(s => s.name));
      } else {
        setPipelineStages(DEFAULT_PIPELINE_STAGES);
        setVisibleStatuses(DEFAULT_PIPELINE_STAGES.map(s => s.name));
      }

      // Reveal credits
      if (data.reveal_credits) setRevealCredits(data.reveal_credits);

      // ICP quality indicator (already shaped by backend)
      const icp = data.icp_quality;
      if (icp?.present && icp?.source) {
        setIcpQuality({
          method: icp.source?.method || 'unknown',
          apolloEnriched: icp.source?.apollo_enriched || false,
          hasSearchParams: !!icp.has_search_params,
        });
      }
    };

    (async () => {
      try {
        const res = await leadsAPI.getBootstrap();
        hydrateFromBootstrap(res.data);
      } catch (err) {
        // Fallback: load individually so the page still works
        console.warn('[Leads] _bootstrap failed, falling back to individual loaders', err?.message);
        if (cancelled) return;
        loadTeamMembers();
        loadLeadSources();
        loadLeadStatuses();
        loadRevealCredits();
        try {
          const ic = await api.get('/api/icp/');
          const icpData = ic.data?.icp;
          if (!cancelled && icpData?.source) {
            setIcpQuality({
              method: icpData.source?.method || 'unknown',
              apolloEnriched: icpData.source?.apollo_enriched || false,
              hasSearchParams: !!icpData.apollo_search_params,
            });
          }
        } catch { /* ICP not available yet */ }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Handle onboarding flow when redirected from Get Started
  useEffect(() => {
    const newLeadId = searchParams.get('newLead');
    const isOnboarding = searchParams.get('onboarding') === 'true';
    
    if (newLeadId && isOnboarding && !loading && leads.length > 0) {
      // Clear URL params without triggering navigation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('newLead');
      newParams.delete('onboarding');
      setSearchParams(newParams, { replace: true });
      
      // Check if Sales is the primary module before showing guided tour
      const checkPrimaryModule = async () => {
        try {
          const res = await api.get('/api/onboarding/status');
          const data = res.data;
          if (data.primary_module !== 'sales') {
            return;
          }
        } catch (err) {
          console.error('[Leads] Failed to check primary module:', err);
        }
        
        setHighlightedLeadId(newLeadId);
        
        setTimeout(() => {
          const cardElement = document.querySelector(`[data-testid="lead-card-${newLeadId}"]`);
          if (cardElement) {
            cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            highlightedCardRef.current = cardElement;
          }
          
          setShowConfetti(true);
          
          setTimeout(() => {
            setOnboardingStep('highlight');
          }, 800);
        }, 500);
      };
      
      checkPrimaryModule();
    }
  }, [searchParams, loading, leads, setSearchParams]);

  // Handle confetti completion
  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  // Handle tooltip dismissals - Skip onboarding
  const handleDismissOnboarding = useCallback(async () => {
    setOnboardingStep(null);
    setHighlightedLeadId(null);
    setShowConfetti(false);
    
    try {
      await api.post('/api/onboarding/skip');
    } catch (err) {
      console.error('Failed to skip onboarding:', err);
    }
    
    toast.success("You're all set to explore!", {
      description: 'You can resume setup anytime from the banner.',
      duration: 3000,
    });
  }, []);

  // Poll for AI call completion
  const startCallPolling = useCallback((leadId, callId) => {
    setActiveCallId(callId);
    setActiveCallLeadId(leadId);
    setCallPolling(true);

    // Clear any existing interval
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    let pollCount = 0;
    const maxPolls = 60; // 5 minutes max (5s interval)

    pollIntervalRef.current = setInterval(async () => {
      pollCount++;
      if (pollCount > maxPolls) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setCallPolling(false);
        setActiveCallId(null);
        setActiveCallLeadId(null);
        return;
      }

      try {
        const res = await leadsAPI.getAICallStatus(leadId, callId);
        const { status, summary, transcript, duration } = res.data;

        if (status === 'completed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setCallPolling(false);
          setActiveCallId(null);
          setActiveCallLeadId(null);

          // Move lead to Contacted
          try {
            await leadsAPI.completeAICall(leadId);
          } catch (err) {
            handleError('Leads', err, { fallbackMessage: 'Failed to update lead status' });
          }

          // Store summary (backend also creates a call record in lead_calls)
          let callRecordId = null;
          if (summary || transcript) {
            try {
              const summaryRes = await leadsAPI.storeAICallSummary(leadId, {
                call_id: callId,
                summary: summary || '',
                transcript: transcript || '',
                duration: duration || 0,
              });
              callRecordId = summaryRes.data?.call_record_id;
              // Update local summaries cache
              setLeadSummaries(prev => ({
                ...prev,
                [leadId]: [{ summary, transcript, duration, call_record_id: callRecordId, created_at: new Date().toISOString() }, ...(prev[leadId] || [])],
              }));
            } catch (err) {
              console.error('Failed to store summary:', err);
            }
          }

          // Refresh leads to reflect the stage change
          await loadLeads();

          // Find lead name for the toast
          const lead = leads.find(l => l.id === leadId);
          const leadName = lead?.name || 'Lead';

          // Show toast with click-to-view action
          toast.success('AI Call Summary Added', {
            description: `${leadName} moved to Contacted.`,
            duration: 8000,
            action: {
              label: 'View Summary',
              onClick: () => navigate(`/leads/${leadId}?tab=calls${callRecordId ? `&highlight=${callRecordId}` : ''}`),
            },
          });
        } else if (status === 'failed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setCallPolling(false);
          setActiveCallId(null);
          setActiveCallLeadId(null);
          toast.error('AI call failed', { duration: 3000 });
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 5000);
  }, [loadLeads, leads, navigate]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Handle AI call action from onboarding tooltip
  const handleAICallAction = useCallback(async () => {
    const highlightedLead = leads.find(l => l.id === highlightedLeadId);
    if (!highlightedLead) return;

    // Close tooltip immediately
    setOnboardingStep(null);
    setHighlightedLeadId(null);
    setShowConfetti(false);

    if (!highlightedLead.phone) {
      toast.error('This lead has no phone number');
      return;
    }

    toast.loading(`Calling ${highlightedLead.name}...`, { id: 'ai-call-onboarding' });

    try {
      const res = await leadsAPI.initiateAICall(highlightedLeadId);
      toast.success(`AI call initiated to ${res.data.lead_name}`, {
        id: 'ai-call-onboarding',
        description: `Calling ${res.data.phone} — we'll update the lead when the call ends.`,
        duration: 5000,
      });

      // Start polling for call completion
      startCallPolling(highlightedLeadId, res.data.call_id);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to initiate AI call';
      toast.error(msg, { id: 'ai-call-onboarding' });
    }
  }, [highlightedLeadId, leads, startCallPolling]);

  // Check scroll position for kanban scroll indicators
  const checkKanbanScroll = useCallback(() => {
    const container = kanbanScrollRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  // Initialize scroll check when kanban is visible
  useEffect(() => {
    if (view === 'kanban') {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(checkKanbanScroll, 100);
      return () => clearTimeout(timer);
    }
  }, [view, pipelineStages, visibleStatuses, checkKanbanScroll]);

  // Scroll kanban left or right
  const scrollKanban = (direction) => {
    const container = kanbanScrollRef.current;
    if (container) {
      const columnWidth = container.clientWidth / 4.3;
      const scrollAmount = direction === 'left' ? -columnWidth : columnWidth;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const loadLeadSources = async () => {
    try {
      const response = await leadSettingsAPI.getSources();
      setLeadSources(normalizeList(response.data, 'sources'));
    } catch (error) {
      console.error('Failed to load lead sources:', error);
    }
  };

  const loadRevealCredits = async () => {
    try {
      const response = await leadsAPI.getRevealCredits();
      setRevealCredits(response.data);
    } catch (error) {
      console.error('Failed to load reveal credits:', error);
    }
  };

  const handleRevealContact = async (lead, e) => {
    if (e) e.stopPropagation();
    if (revealingLeadId) return;
    
    setRevealingLeadId(lead.id);
    try {
      const response = await leadsAPI.revealContact(lead.id);
      const data = response.data;
      
      if (data.success && data.revealed) {
        const details = [data.email, data.phone].filter(Boolean).join(' | ');
        toast.success(`Contact revealed: ${data.email ? lead.name : lead.name}`, {
          description: details || 'Contact details unlocked',
        });
        await Promise.all([loadLeads(), loadRevealCredits()]);
      } else if (data.success && !data.revealed) {
        // Apollo found person but no email/phone — no credits charged
        toast.warning('No contact info found', {
          description: data.message || 'No email or phone available from provider. No credits charged.',
        });
        await loadLeads(); // Still refresh — name may have been unmasked
      } else {
        toast.error(data.message || 'Failed to reveal contact');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to reveal contact';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits', {
          description: 'Purchase more reveal credits to unlock contacts.',
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setRevealingLeadId(null);
    }
  };

  const handleBulkRevealColumn = async (columnName, unrevealedCount) => {
    if (bulkRevealColumn) return; // Already revealing
    const costPerReveal = 0.065;
    const totalCost = (unrevealedCount * costPerReveal).toFixed(2);
    setBulkRevealConfirm({ columnName, count: unrevealedCount, cost: totalCost });
  };

  const confirmBulkReveal = async () => {
    if (!bulkRevealConfirm) return;
    const { columnName } = bulkRevealConfirm;
    setBulkRevealConfirm(null);
    setBulkRevealColumn(columnName);
    try {
      const response = await leadsAPI.bulkRevealColumn(columnName);
      const data = response.data;
      if (data.revealed > 0) {
        toast.success(`Revealed ${data.revealed} contacts in "${columnName}"`, {
          description: data.failed > 0 ? `${data.failed} failed` : undefined,
        });
        await Promise.all([loadLeads(), loadRevealCredits()]);
      } else {
        toast.info(data.message || 'No contacts revealed');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Bulk reveal failed';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits', {
          description: detail,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setBulkRevealColumn(null);
    }
  };

  const handleBulkIcpReveal = () => {
    const unrevealed = leads.filter(l => !l.contact_revealed && !l.email && !l.phone && (l.apollo_id || l.provider_id || (l.source || '').toLowerCase().includes('apollo')));
    const scoredLeads = unrevealed.map(l => {
      const icpTotal = l.icp_score?.total || 0;
      const aiIcpScore = l.ai_icp_match?.score || 0;
      return { ...l, _bestIcpScore: Math.max(icpTotal, aiIcpScore) };
    }).filter(l => l._bestIcpScore > 0);
    scoredLeads.sort((a, b) => b._bestIcpScore - a._bestIcpScore);
    const top5 = scoredLeads.slice(0, 5);
    if (top5.length === 0) {
      toast.info('No unrevealed leads with ICP scores found');
      return;
    }
    setBulkIcpRevealOpen(top5);
  };

  const confirmBulkIcpReveal = async () => {
    if (!Array.isArray(bulkIcpRevealOpen) || bulkIcpRevealOpen.length === 0) return;
    const leadIds = bulkIcpRevealOpen.map(l => l.id);
    setBulkIcpRevealing(true);
    try {
      const response = await leadsAPI.bulkReveal(leadIds);
      const data = response.data;
      if (data.revealed > 0) {
        toast.success(`Revealed ${data.revealed} top ICP contacts`, {
          description: data.failed > 0 ? `${data.failed} failed` : 'All contacts revealed successfully',
        });
        await Promise.all([loadLeads(), loadRevealCredits()]);
      } else {
        toast.info('No contacts could be revealed');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Bulk reveal failed';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits', { description: detail });
      } else {
        toast.error(detail);
      }
    } finally {
      setBulkIcpRevealing(false);
      setBulkIcpRevealOpen(false);
    }
  };

  const handleDiscoverLeads = async () => {
    setDiscovering(true);
    setDiscoveredContacts([]);
    setSelectedDiscovered(new Set());
    try {
      const response = await leadsAPI.discoverLeads();
      const data = response.data;
      setDiscoveredContacts(data.contacts || []);
      if (data.contacts?.length === 0) {
        toast.info('No new contacts found matching your ICP');
      } else {
        toast.success(`Found ${data.new_contacts} new contacts`);
      }
      // Show auto-reveal notification
      if (data.auto_revealed_count > 0) {
        toast.success(`${data.auto_revealed_count} Grade A leads auto-revealed`, {
          description: `Top ICP matches (85%+) revealed and imported automatically.`,
          duration: 6000,
        });
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Discovery failed';
      toast.error(detail);
    } finally {
      setDiscovering(false);
    }
  };

  const handleImportSelected = async () => {
    const selected = discoveredContacts.filter((_, i) => selectedDiscovered.has(i));
    if (selected.length === 0) {
      toast.error('Select at least one contact to import');
      return;
    }
    setImporting(true);
    try {
      const response = await leadsAPI.importDiscoveredLeads(selected);
      toast.success(`Imported ${response.data.imported} leads`, {
        description: 'Leads added as locked contacts. Reveal to unlock email & phone.',
      });
      setDiscoverOpen(false);
      setDiscoveredContacts([]);
      setSelectedDiscovered(new Set());
      await loadLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const toggleDiscoveredSelection = (index) => {
    setSelectedDiscovered(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAllDiscovered = () => {
    if (selectedDiscovered.size === discoveredContacts.length) {
      setSelectedDiscovered(new Set());
    } else {
      setSelectedDiscovered(new Set(discoveredContacts.map((_, i) => i)));
    }
  };

  const loadLeadStatuses = async () => {
    // Show default stages instantly so kanban renders immediately
    if (pipelineStages.length === 0) {
      setPipelineStages(DEFAULT_PIPELINE_STAGES);
      setVisibleStatuses(DEFAULT_PIPELINE_STAGES.map(s => s.name));
    }
    try {
      // Load pipeline stages for Kanban columns
      const response = await leadSettingsAPI.getStages();
      const stages = normalizeList(response.data, 'stages');
      // Sort by order
      const sortedStages = stages.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Update with fetched stages if we got results
      if (sortedStages.length > 0) {
        setPipelineStages(sortedStages);
        setVisibleStatuses(prev => prev.length > 0 ? prev : sortedStages.filter(s => s.visible !== false).map(s => s.name));
      }
    } catch (error) {
      console.error('Failed to load pipeline stages:', error);
      // Defaults already set above
    }
  };

  const loadTeamMembers = async () => {
    try {
      // Use getTeamMembers instead of getUsers - doesn't require HR permissions
      const response = await hrAPI.getTeamMembers();
      
      // Use normalizeList to handle various response formats
      const usersArray = normalizeList(response.data, 'users');
      
      // Map users to match the expected format
      const users = usersArray.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email
      }));
      
      // Also include current user if not already in list
      if (user && !users.find(m => m.id === user.id)) {
        users.push({ id: user.id, name: user.name, email: user.email });
      }
      
      setTeamMembers(users);
    } catch (error) {
      console.error('Failed to load team members:', error);
      // Fallback: at least include current user
      if (user) {
        setTeamMembers([{ id: user.id, name: user.name, email: user.email }]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) return;
    setSubmitting(true);
    setDuplicateError('');
    
    try {
      if (editingLead) {
        // Preserve existing address data - don't send empty values that will overwrite
        const updateData = { ...formData };
        
        // Remove address fields if they're empty (preserve existing values in DB)
        if (!formData.address) delete updateData.address;
        if (!formData.city) delete updateData.city;
        if (!formData.state) delete updateData.state;
        if (!formData.postal_code) delete updateData.postal_code;
        if (!formData.country) delete updateData.country;
        
        await leadsAPI.update(editingLead.id, updateData);
        toast.success('Lead updated successfully');
      } else {
        await leadsAPI.create(formData);
        toast.success('Lead created successfully');
      }
      setDialogOpen(false);
      resetForm();
      setDuplicateError('');
      loadLeads();
    } catch (error) {
      // Handle 409 Conflict (duplicate email/phone) with inline error
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail || 'A lead with this email or phone number already exists.';
        setDuplicateError(detail);
        return;
      }
      
      handleError('Leads', error, { silent: true, fallbackMessage: 'Failed to save lead' });
      // Extract meaningful error message from backend response
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save lead';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation errors
        const firstError = errorDetail[0];
        const field = firstError.loc?.slice(-1)[0] || 'field';
        errorMessage = `${field}: ${firstError.msg}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    
    // Normalize status, source, and assigned_to to match API format
    const normalizedStatus = pipelineStages.find(s => s.name.toLowerCase() === lead.status?.toLowerCase())?.name || lead.status || 'new';
    const normalizedSource = leadSources.find(s => s.name.toLowerCase() === lead.source?.toLowerCase())?.name || lead.source || '';
    const normalizedAssignedTo = teamMembers.find(m => m.id === lead.assigned_to)?.id || lead.assigned_to || '';
    
    setFormData({
      ...lead,
      status: normalizedStatus,
      source: normalizedSource,
      assigned_to: normalizedAssignedTo
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await leadsAPI.delete(id);
        toast.success('Lead deleted successfully');
        loadLeads();
      } catch (error) {
        toast.error('Failed to delete lead');
      }
    }
  };

  // ── Bulk select + delete (List/Table view only) ──
  const [selectedLeadIds, setSelectedLeadIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  const [notRelevantOpen, setNotRelevantOpen] = useState(false);
  const [notRelevantLeads, setNotRelevantLeads] = useState([]);
  const [nrLoading, setNrLoading] = useState(false);
  const [nrSelected, setNrSelected] = useState(new Set());
  const [nrClearing, setNrClearing] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSelected, setAssignSelected] = useState([]);   // assignee user ids
  const [assignMode, setAssignMode] = useState('single');     // 'single' | 'round_robin'
  const [assigning, setAssigning] = useState(false);

  const clearSelection = useCallback(() => { setSelectedLeadIds(new Set()); setSelectAllMatching(false); }, []);

  const toggleSelectLead = useCallback((id) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((pageIds) => {
    setSelectedLeadIds((prev) => {
      const allSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  // Builds the filter payload for "select all N matching" bulk ops (delete/move/assign)
  // so the action hits the ENTIRE filtered set, not just the current page's rows.
  const buildMatchingFilters = () => {
    const f = {};
    if (filters.status) f.status = filters.status;
    if (filters.assignedTo) f.assigned_to = filters.assignedTo;
    if (filters.source) f.source = filters.source;
    if (debouncedSearch) f.search = debouncedSearch;
    return f;
  };

  const handleBulkDelete = async () => {
    const payload = selectAllMatching
      ? { select_all_matching: true, filters: buildMatchingFilters() }
      : { lead_ids: Array.from(selectedLeadIds) };
    if (!selectAllMatching && payload.lead_ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await leadsAPI.bulkDelete(payload);
      const deleted = res?.deleted_count ?? 0;
      const skipped = res?.skipped_count ?? 0;
      toast.success(`Deleted ${deleted} lead${deleted !== 1 ? 's' : ''}${skipped ? ` · ${skipped} skipped` : ''}`);
      clearSelection();
      setBulkDeleteOpen(false);
      loadLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete leads');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openNotRelevant = async () => {
    setNotRelevantOpen(true);
    setNrLoading(true);
    setNrSelected(new Set());
    try {
      const res = await leadsAPI.getNotRelevant({ limit: 100 });
      setNotRelevantLeads(res.data?.leads || []);
    } catch (e) {
      toast.error('Could not load the Not Relevant lane');
    } finally {
      setNrLoading(false);
    }
  };

  const toggleNrSelect = (id) => {
    setNrSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkClear = async (all = false) => {
    const ids = Array.from(nrSelected);
    const count = all ? notRelevantLeads.length : ids.length;
    if (count === 0) return;
    if (!window.confirm(`Permanently delete ${count} quarantined ${count === 1 ? 'lead' : 'leads'}? This can't be undone.`)) return;
    setNrClearing(true);
    try {
      const res = await leadsAPI.bulkClearNotRelevant(all ? [] : ids, all);
      toast.success(`Cleared ${res.data?.deleted ?? count} spam ${(res.data?.deleted ?? count) === 1 ? 'lead' : 'leads'}`);
      setNrSelected(new Set());
      const refreshed = await leadsAPI.getNotRelevant({ limit: 100 });
      setNotRelevantLeads(refreshed.data?.leads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear leads');
    } finally {
      setNrClearing(false);
    }
  };

  const handleRestoreRelevant = async (lead) => {
    try {
      await leadsAPI.restoreRelevant(lead.id);
      toast.success(`${lead.name || 'Lead'} restored to the pipeline`);
      setNotRelevantLeads((prev) => prev.filter((l) => l.id !== lead.id));
      loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not restore lead');
    }
  };


  const handleBulkMoveAudience = async () => {
    const ids = Array.from(selectedLeadIds);
    const payload = selectAllMatching
      ? { select_all_matching: true, filters: buildMatchingFilters() }
      : { lead_ids: ids };
    if (!selectAllMatching && ids.length === 0) return;
    setBulkMoving(true);
    try {
      const res = await leadsAPI.bulkMoveToAudience(payload);
      const moved = res?.moved_count ?? 0;
      const skipped = res?.skipped_count ?? 0;
      const paused = res?.cadences_paused ?? 0;
      const movedIds = [...ids];
      // Undo is only offered for an explicit selection (we don't hold ids for select-all).
      const canUndo = moved > 0 && !selectAllMatching;
      toast.success(
        `Moved ${moved} lead${moved !== 1 ? 's' : ''} to Audience${paused ? ` · ${paused} cadence${paused !== 1 ? 's' : ''} paused` : ''}${skipped ? ` · ${skipped} skipped` : ''}`,
        {
          action: canUndo ? {
            label: 'Undo',
            onClick: async () => {
              try {
                const u = await leadsAPI.bulkMoveToAudienceUndo(movedIds);
                toast.success(`Restored ${u?.restored_count ?? 0} lead${(u?.restored_count ?? 0) !== 1 ? 's' : ''} to the pipeline`);
                loadLeads();
              } catch {
                toast.error('Could not undo — please restore from the Audience page');
              }
            },
          } : undefined,
        }
      );
      clearSelection();
      setBulkMoveOpen(false);
      loadLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to move leads to Audience');
    } finally {
      setBulkMoving(false);
    }
  };

  const handleBulkAssignConfirm = async () => {
    if (assignSelected.length === 0) { toast.error('Pick at least one teammate'); return; }
    setAssigning(true);
    try {
      const payload = assignMode === 'round_robin' && assignSelected.length > 1
        ? { assignee_ids: assignSelected, mode: 'round_robin' }
        : { assignee_ids: [assignSelected[0]], mode: 'single' };
      if (selectAllMatching) {
        payload.select_all_matching = true;
        payload.filters = {};
        if (filters.status) payload.filters.status = filters.status;
        if (filters.assignedTo) payload.filters.assigned_to = filters.assignedTo;
        if (filters.source) payload.filters.source = filters.source;
        if (debouncedSearch) payload.filters.search = debouncedSearch;
      } else {
        payload.lead_ids = Array.from(selectedLeadIds);
        if (payload.lead_ids.length === 0) { toast.error('No leads selected'); setAssigning(false); return; }
      }
      const res = await leadsAPI.bulkAssign(payload);
      toast.success(`Assigned ${res?.assigned_count ?? 0} lead${(res?.assigned_count ?? 0) !== 1 ? 's' : ''}`);
      setAssignOpen(false);
      setAssignSelected([]);
      clearSelection();
      loadLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to assign leads');
    } finally {
      setAssigning(false);
    }
  };

  // Clear the selection whenever the user switches view (table ⇄ kanban) or page,
  // so a stale cross-view selection can't linger. Selection now works in BOTH views.
  useEffect(() => {
    clearSelection();
  }, [view, clearSelection]);
  useEffect(() => {
    clearSelection();
  }, [currentPage, pageSize, debouncedSearch, clearSelection]);

  // Reset to page 1 whenever the status filter changes so server-side pagination
  // starts from the top of the newly-filtered set.
  const prevStatusFilterRef = useRef(filters.status);
  useEffect(() => {
    if (prevStatusFilterRef.current !== filters.status) {
      prevStatusFilterRef.current = filters.status;
      setCurrentPage(1);
    }
  }, [filters.status]);

  // Optimistic status change - updates UI instantly, syncs backend in background
  const handleStatusChange = async (leadId, newStatus, previousStatus = null) => {
    // Store the previous status for potential rollback
    const leadToUpdate = leads.find(l => l.id === leadId);
    const oldStatus = previousStatus || leadToUpdate?.status;
    
    // Optimistic update - move dragged lead to front of array so it appears at top of destination column
    setLeads(prevLeads => {
      const updated = prevLeads.find(lead => lead.id === leadId);
      if (!updated) return prevLeads;
      const others = prevLeads.filter(lead => lead.id !== leadId);
      return [{ ...updated, status: newStatus }, ...others];
    });
    
    try {
      // Background API call
      await leadsAPI.update(leadId, { status: newStatus });
      // Success - no need to reload, UI is already updated
    } catch (error) {
      // Rollback on failure - revert to previous status (keep at top, just restore status)
      setLeads(prevLeads => {
        const updated = prevLeads.find(lead => lead.id === leadId);
        if (!updated) return prevLeads;
        const others = prevLeads.filter(lead => lead.id !== leadId);
        return [{ ...updated, status: oldStatus }, ...others];
      });
      
      const detail = error?.response?.data?.detail;
      const statusCode = error?.response?.status;
      console.error('Status update error:', statusCode, detail, error);
      if (statusCode === 403) {
        toast.error('Permission denied', { description: 'You don\'t have permission to update this lead.' });
      } else if (statusCode === 404) {
        toast.error('Lead not found', { description: 'This lead may have been deleted.' });
      } else {
        toast.error(detail || 'Failed to update status');
      }
    }
  };

  const handlePreview = (lead) => {
    setPreviewLead(lead);
    setPreviewOpen(true);
  };

  const handleViewDetails = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const downloadSampleTemplate = () => {
    try {
      // Create sample data with headers and one example row
      const sampleData = [
        {
          name: 'John Doe',
          email: 'john.doe@example.com',
          phone: '+1-555-0123',
          company: 'Acme Corporation',
          status: 'New Lead',
          source: 'Website',
          value: '5000',
          notes: 'Interested in our premium service package',
          address: '123 Main Street',
          city: 'New York',
          state: 'NY',
          zip_code: '10001'
        },
        {
          name: 'Jane Smith',
          email: 'jane.smith@example.com',
          phone: '+1-555-0124',
          company: 'Tech Solutions Inc',
          status: 'contacted',
          source: 'Referral',
          value: '7500',
          notes: 'Looking for enterprise solution',
          address: '456 Oak Avenue',
          city: 'Los Angeles',
          state: 'CA',
          zip_code: '90001'
        }
      ];

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(sampleData);
      
      // Set column widths for better readability
      ws['!cols'] = [
        { wch: 20 }, // name
        { wch: 30 }, // email
        { wch: 15 }, // phone
        { wch: 25 }, // company
        { wch: 12 }, // status
        { wch: 15 }, // source
        { wch: 10 }, // value
        { wch: 40 }, // notes
        { wch: 25 }, // address
        { wch: 15 }, // city
        { wch: 8 },  // state
        { wch: 10 }  // zip_code
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Leads Sample');

      // Generate Excel file and trigger download
      XLSX.writeFile(wb, 'leads_import_template.xlsx');
      
      toast.success('Sample template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download sample template');
    }
  };

  // Import reset function
  const resetImport = useCallback(() => {
    setImportFile(null);
    setImportData([]);
    setImportStatus('idle');
    setImportProgress(0);
    setImportResult(null);
    setIsDraggingFile(false);
    setDuplicateCheck(null);
    setDuplicateStrategy('update');
    setSkipAiScoring(false);
    setCheckingDuplicates(false);
    setDuplicateCheckFailed(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // File selection handler with preview
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;
    
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const isValidType = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType) {
      toast.error('Invalid file type. Please upload .xlsx, .xls, or .csv files only.');
      return;
    }
    
    setImportFile(file);
    setImportStatus('parsing');
    setImportProgress(10);
    
    try {
      const data = await file.arrayBuffer();
      setImportProgress(30);
      
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      setImportProgress(50);
      
      if (jsonData.length === 0) {
        toast.error('The file appears to be empty or has no valid data.');
        setImportStatus('error');
        return;
      }
      
      // Ensure we have valid stages loaded
      if (pipelineStages.length === 0) {
        await loadLeadStatuses();
      }
      
      // Create status mapping using pipeline stages
      const statusMap = {};
      pipelineStages.forEach(status => {
        statusMap[status.name.toLowerCase()] = status.name;
      });
      const defaultStatus = pipelineStages.length > 0 ? pipelineStages[0].name : 'New Lead';
      
      setImportProgress(70);
      
      // Transform rows: normalize headers (trim + lowercase), map via aliases,
      // sanitize money so a stray "$1,200" / "N/A" can never become NaN.
      const mappedData = jsonData.map((row, index) => {
        const normRow = {};
        Object.keys(row || {}).forEach((k) => {
          normRow[String(k).trim().toLowerCase()] = row[k];
        });
        const rawStatus = String(getImportField(normRow, 'status') || '').toLowerCase();
        const matchedStatus = statusMap[rawStatus] || defaultStatus;
        const name = getImportField(normRow, 'name');
        const email = getImportField(normRow, 'email');
        const reason = !name
          ? 'Missing name'
          : (!isValidEmail(email) ? 'Invalid email' : '');
        return {
          row_number: index + 2,
          name,
          email,
          phone: getImportField(normRow, 'phone'),
          company: getImportField(normRow, 'company'),
          status: matchedStatus,
          source: getImportField(normRow, 'source'),
          value: parseImportMoney(getImportField(normRow, 'value')),
          notes: getImportField(normRow, 'notes'),
          address: getImportField(normRow, 'address'),
          city: getImportField(normRow, 'city'),
          state: getImportField(normRow, 'state'),
          zip_code: getImportField(normRow, 'zip_code'),
          product: getImportField(normRow, 'product'),
          _valid: !!name,
          _reason: reason,
        };
      });
      
      // A row is importable if it has a name (email is flagged but not blocking).
      const validData = mappedData.filter(row => row._valid);
      const skippedCount = mappedData.length - validData.length;
      const invalidEmailCount = validData.filter(r => r._reason === 'Invalid email').length;
      
      if (validData.length === 0) {
        toast.error('No valid leads found. Ensure your file has a "name" column.');
        setImportStatus('error');
        return;
      }
      
      setImportProgress(100);
      setImportData(validData);
      setImportStatus('preview');
      let summary = `Found ${validData.length} lead${validData.length === 1 ? '' : 's'} ready to import`;
      if (skippedCount > 0) summary += ` · ${skippedCount} skipped (missing name)`;
      if (invalidEmailCount > 0) summary += ` · ${invalidEmailCount} with invalid email`;
      toast.success(summary);

      // Phase 2 — duplicate detection dry-run (against existing leads + within file).
      setDuplicateCheck(null);
      setDuplicateStrategy('update');
      setDuplicateCheckFailed(false);
      setCheckingDuplicates(true);
      try {
        const leadsToCheck = validData.map(lead => ({
          name: lead.name,
          email: lead.email || null,
          phone: lead.phone || null,
        }));
        // Soft 12s cap: if the check is slow (cold start), don't block the import —
        // the backend safely defaults to the update/merge strategy.
        const dupResp = await Promise.race([
          leadsAPI.bulkImportCheck({ leads: leadsToCheck }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('dup-check-timeout')), 12000)),
        ]);
        setDuplicateCheck(dupResp.data);
      } catch (dupErr) {
        // Non-blocking: if the check fails/times out, import still works (defaults to update).
        setDuplicateCheck(null);
        setDuplicateCheckFailed(true);
      } finally {
        setCheckingDuplicates(false);
      }
      
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error('Failed to parse file. Please check the file format.');
      setImportStatus('error');
    }
  }, [pipelineStages, loadLeadStatuses]);

  // Drag and drop handlers
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  }, []);

  const handleFileDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  }, []);

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Import leads to backend — Phase 3: chunked upload + real progress + AI-budget transparency
  const handleImportLeads = async () => {
    if (importData.length === 0) {
      toast.error('No leads to import');
      return;
    }

    setImportStatus('importing');
    setImportProgress(0);

    try {
      const leadsToImport = importData.map(lead => ({
        name: lead.name,
        email: lead.email || null,
        phone: lead.phone || null,
        company: lead.company || null,
        status: lead.status,
        source: lead.source || null,
        value: lead.value,
        notes: lead.notes || null,
        address: lead.address || null,
        city: lead.city || null,
        state: lead.state || null,
        zip_code: lead.zip_code || null,
      }));

      // Chunk large imports so a single request never hits proxy/timeout limits.
      // Chunks run sequentially so cross-chunk duplicates are caught against the
      // leads inserted by earlier chunks.
      const CHUNK_SIZE = 200;
      const chunks = [];
      for (let i = 0; i < leadsToImport.length; i += CHUNK_SIZE) {
        chunks.push(leadsToImport.slice(i, i + CHUNK_SIZE));
      }

      const agg = {
        success_count: 0, error_count: 0, updated_count: 0, skipped_count: 0,
        audience_relisted_count: 0, pipeline_kept_count: 0,
        queued_for_ai: 0, ai_pending: 0, errors: [],
        credit_balance: 0, ai_total_affordable: -1, ai_budget_exhausted: false,
      };
      let aiAlreadyQueued = 0;

      // Stable batch name for the whole CSV (all chunks share it) so imports don't
      // fragment into multiple Audience "lists". Prefer the uploaded file name.
      const _batchName = (importFile && importFile.name)
        ? importFile.name
        : `import_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').replace(/(\d{8})(\d{6})/, '$1_$2')}.csv`;

      for (let ci = 0; ci < chunks.length; ci++) {
        const resp = await leadsAPI.bulkImport({
          leads: chunks[ci],
          duplicate_strategy: duplicateStrategy,
          ai_already_queued: aiAlreadyQueued,
          import_batch: _batchName,
          skip_ai_scoring: skipAiScoring,
        });
        const d = resp.data || {};
        agg.success_count += d.success_count || 0;
        agg.error_count += d.error_count || 0;
        agg.updated_count += d.updated_count || 0;
        agg.skipped_count += d.skipped_count || 0;
        agg.audience_relisted_count += d.audience_relisted_count || 0;
        agg.pipeline_kept_count += d.pipeline_kept_count || 0;
        agg.queued_for_ai += d.queued_for_ai || 0;
        agg.ai_pending += d.ai_pending || 0;
        if (Array.isArray(d.errors) && d.errors.length) agg.errors.push(...d.errors);
        // Last-seen budget facts (balance is stable during an async-scored import)
        agg.credit_balance = d.credit_balance ?? agg.credit_balance;
        agg.ai_total_affordable = d.ai_total_affordable ?? agg.ai_total_affordable;
        if (d.ai_budget_exhausted) agg.ai_budget_exhausted = true;
        aiAlreadyQueued += d.queued_for_ai || 0;
        // Real progress bar across chunks
        setImportProgress(Math.round(((ci + 1) / chunks.length) * 100));
      }

      setImportProgress(100);
      setImportResult(agg);
      setImportStatus('complete');

      const { success_count = 0, updated_count = 0, skipped_count = 0, error_count = 0,
              audience_relisted_count = 0, pipeline_kept_count = 0 } = agg;
      if (success_count > 0 || updated_count > 0) {
        const parts = [];
        if (success_count > 0) parts.push(`${success_count} imported`);
        if (updated_count > 0) parts.push(`${updated_count} updated`);
        if (skipped_count > 0) parts.push(`${skipped_count} skipped`);
        toast.success(parts.join(' · '), {
          description: pipeline_kept_count > 0
            ? `${pipeline_kept_count} active pipeline lead${pipeline_kept_count === 1 ? '' : 's'} updated but kept on the Kanban${audience_relisted_count > 0 ? ` · ${audience_relisted_count} moved into this list` : ''}`
            : (audience_relisted_count > 0 ? `${audience_relisted_count} existing lead${audience_relisted_count === 1 ? '' : 's'} moved into this list` : undefined),
        });
        loadLeads();
      } else if (skipped_count > 0) {
        toast.info(`${skipped_count} duplicate lead${skipped_count === 1 ? '' : 's'} skipped`);
      }

      if (error_count > 0) {
        toast.warning(`${error_count} leads failed to import`);
      }

    } catch (error) {
      handleError('Import', error, { fallbackMessage: 'Failed to import leads' });
      toast.error('Failed to import leads');
      setImportStatus('error');
    }
  };

  const handleOpenEmailDialog = (lead) => {
    setQuickActionLead(lead);
    setEmailForm({ subject: `Follow up: ${lead.name}`, body: '' });
    setEmailDialogOpen(true);
  };

  const handleOpenNoteDialog = (lead) => {
    setQuickActionLead(lead);
    setNoteForm({ text: '' });
    setNoteDialogOpen(true);
  };

  const handleOpenCallDialog = (lead) => {
    setQuickActionLead(lead);
    setCallForm({ duration: '', notes: '' });
    setCallDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await leadActivitiesAPI.createEmail({
        lead_id: quickActionLead.id,
        subject: emailForm.subject,
        body: emailForm.body
      });
      toast.success('Email sent successfully');
      setEmailDialogOpen(false);
      setEmailForm({ subject: '', body: '' });
    } catch (error) {
      handleError('Leads', error, { fallbackMessage: 'Failed to send email' });
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleSaveNote = async () => {
    if (!noteForm.text.trim()) {
      toast.error('Please enter note text');
      return;
    }
    try {
      await leadActivitiesAPI.createNote({
        lead_id: quickActionLead.id,
        text: noteForm.text
      });
      toast.success('Note added successfully');
      setNoteDialogOpen(false);
      setNoteForm({ text: '' });
    } catch (error) {
      handleError('Leads', error, { fallbackMessage: 'Failed to save note' });
      toast.error(error.response?.data?.detail || 'Failed to save note');
    }
  };

  const handleSaveCall = async () => {
    if (!callForm.duration || !callForm.notes.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await leadActivitiesAPI.createCall({
        lead_id: quickActionLead.id,
        duration: callForm.duration, // Keep as string
        notes: callForm.notes,
        call_type: 'outgoing'
      });
      toast.success('Call logged successfully');
      setCallDialogOpen(false);
      setCallForm({ duration: '', notes: '' });
    } catch (error) {
      handleError('Leads', error, { fallbackMessage: 'Failed to log call' });
      toast.error(error.response?.data?.detail || 'Failed to log call');
    }
  };

  // AI Actions handlers
  const loadAIStatus = async (leadId) => {
    try {
      const response = await leadsAPI.getAIStatus(leadId);
      setAiStatus(response.data);
    } catch (error) {
      console.error('Failed to load AI status:', error);
      setAiStatus(null);
    }
  };

  const loadAIActivities = async (leadId) => {
    try {
      const response = await leadsAPI.getAIActivities(leadId);
      setAiActivities(response.data.ai_activities || []);
    } catch (error) {
      console.error('Failed to load AI activities:', error);
      setAiActivities([]);
    }
  };

  const handleRunSalesAI = async (lead) => {
    if (!lead?.id) return;
    
    setAiLoading(true);
    try {
      const response = await leadsAPI.runSalesAI(lead.id, { action_type: 'full_playbook' });
      const result = response.data;
      
      if (result.success) {
        const modeLabel = result.mode === 'SIMULATED' ? '(SIMULATED)' : '(LIVE)';
        toast.success(`Sales AI executed ${modeLabel}`, {
          description: `${result.actions_executed.length} action(s) completed`
        });
        // Reload AI activities to show the new execution
        await loadAIActivities(lead.id);
        await loadAIStatus(lead.id);
      } else {
        toast.error('Sales AI Blocked', {
          description: result.blocked_reason || 'Unknown reason'
        });
      }
    } catch (error) {
      handleError('SalesAI', error, { fallbackMessage: 'Failed to run AI analysis' });
      const errorMsg = error.response?.data?.detail || 'Failed to execute Sales AI';
      toast.error(errorMsg);
    } finally {
      setAiLoading(false);
    }
  };

  // Initiate AI outbound call to any lead (from kanban cards, preview, etc.)
  const handleInitiateAICall = async (lead) => {
    if (!lead?.phone) {
      toast.error('This lead has no phone number');
      return;
    }
    const toastId = `ai-call-${lead.id}`;
    toast.loading(`Calling ${lead.name}...`, { id: toastId });
    try {
      const res = await leadsAPI.initiateAICall(lead.id);
      toast.success(`AI call initiated to ${res.data.lead_name}`, {
        id: toastId,
        description: `Calling ${res.data.phone} — we'll update the lead when the call ends.`,
        duration: 5000,
      });
      // Start polling for call completion
      startCallPolling(lead.id, res.data.call_id);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to initiate AI call';
      toast.error(msg, { id: toastId });
    }
  };

  // Load AI data when preview opens
  useEffect(() => {
    if (previewLead?.id) {
      loadAIStatus(previewLead.id);
      loadAIActivities(previewLead.id);
    }
  }, [previewLead?.id]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      website: '',
      country: '',
      city: '',
      status: 'New Lead',
      source: '',
      value: 0,
      notes: '',
    });
    setEditingLead(null);
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
    
    // Set drag image with slight offset for better UX
    if (e.currentTarget) {
      e.dataTransfer.setDragImage(e.currentTarget, 20, 20);
    }
    
    // Add dragging class for smooth visual feedback
    requestAnimationFrame(() => {
      if (e.currentTarget) {
        e.currentTarget.style.opacity = '0.5';
        e.currentTarget.style.transform = 'scale(0.98)';
      }
    });
  };

  const handleDragEnd = (e) => {
    // Reset visual state
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.pointerEvents = 'auto';
    }
    stopKanbanAutoScroll();
    setDraggedLead(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDragEnter = (e, columnName) => {
    e.preventDefault();
    if (draggedLead && draggedLead.status !== columnName) {
      setDragOverColumn(columnName);
    }
  };

  const handleColumnDragLeave = (e, columnName) => {
    // Only clear if we're actually leaving the column (not entering a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (dragOverColumn === columnName) {
        setDragOverColumn(null);
      }
    }
  };

  // Auto-scroll kanban during drag when near edges.
  // Previously this nudged scrollLeft once per dragover event, so holding the
  // pointer still at the edge stopped scrolling. Now the dragover handler only
  // records the desired direction/speed and a requestAnimationFrame loop applies
  // it continuously until the pointer leaves the edge zone or the drag ends.
  const stopKanbanAutoScroll = () => {
    const s = kanbanAutoScrollRef.current;
    if (s.raf) cancelAnimationFrame(s.raf);
    s.raf = null;
    s.dir = 0;
    s.speed = 0;
  };

  const kanbanAutoScrollTick = () => {
    const s = kanbanAutoScrollRef.current;
    const container = kanbanScrollRef.current;
    if (!container || !draggedLeadRef.current || s.dir === 0) {
      s.raf = null;
      return;
    }
    container.scrollLeft += s.dir * s.speed;
    try { checkKanbanScroll(); } catch (_) { /* indicators optional */ }
    s.raf = requestAnimationFrame(kanbanAutoScrollTick);
  };

  const handleKanbanDragOver = (e) => {
    e.preventDefault();

    const container = kanbanScrollRef.current;
    if (!container || !draggedLead) return;

    const containerRect = container.getBoundingClientRect();
    const mouseX = e.clientX;
    const edgeThreshold = 120; // px from edge where auto-scroll engages

    const leftDistance = mouseX - containerRect.left;
    const rightDistance = containerRect.right - mouseX;
    const s = kanbanAutoScrollRef.current;

    // Speed ramps up the closer the pointer is to the edge (8 → 35 px/frame).
    if (leftDistance < edgeThreshold && leftDistance >= 0) {
      s.dir = -1;
      s.speed = Math.max(8, 35 - (leftDistance / edgeThreshold) * 25);
    } else if (rightDistance < edgeThreshold && rightDistance >= 0) {
      s.dir = 1;
      s.speed = Math.max(8, 35 - (rightDistance / edgeThreshold) * 25);
    } else {
      s.dir = 0; // pointer away from edges → idle (loop self-stops)
    }

    if (s.dir !== 0 && !s.raf) {
      s.raf = requestAnimationFrame(kanbanAutoScrollTick);
    }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    stopKanbanAutoScroll();
    
    if (draggedLead && draggedLead.status !== newStatus) {
      const previousStatus = draggedLead.status;
      
      // Optimistically update kanbanData for instant UI feedback
      if (kanbanData) {
        setKanbanData(prev => {
          if (!prev) return prev;
          const updated = { ...prev, columns: { ...prev.columns } };
          // Remove from old column
          const fromName = Object.keys(updated.columns).find(
            s => s.toLowerCase() === (previousStatus || '').toLowerCase()
          );
          // Add to new column
          const toName = Object.keys(updated.columns).find(
            s => s.toLowerCase() === newStatus.toLowerCase()
          );
          if (fromName && updated.columns[fromName]) {
            updated.columns[fromName] = {
              ...updated.columns[fromName],
              leads: updated.columns[fromName].leads.filter(l => l.id !== draggedLead.id),
              total: Math.max(0, updated.columns[fromName].total - 1),
            };
          }
          if (toName && updated.columns[toName]) {
            const movedLead = { ...draggedLead, status: newStatus, pipeline_stage: newStatus };
            updated.columns[toName] = {
              ...updated.columns[toName],
              leads: [movedLead, ...updated.columns[toName].leads],
              total: updated.columns[toName].total + 1,
            };
          }
          return updated;
        });
      }
      
      await handleStatusChange(draggedLead.id, newStatus, previousStatus);
    }
    setDraggedLead(null);
    setDragOverColumn(null);
  };

  // Calculate filtered leads first (needed for render decisions)
  const filteredLeads = leads.filter(lead => {
    // Search filter — matches name, email, company, phone
    const q = search.toLowerCase().trim();
    const matchesSearch = !q ||
      lead.name?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q) ||
      lead.company?.toLowerCase().includes(q) ||
      lead.phone?.toLowerCase().includes(q);
    
    // Status filter (drift-tolerant: normalize both sides to a canonical stage)
    const matchesStatus = !filters.status ||
      toStage(lead.status) === toStage(filters.status);
    
    // Source filter
    const matchesSource = !filters.source || lead.source === filters.source;
    
    // Assigned To filter
    const matchesAssignedTo = !filters.assignedTo || 
      (filters.assignedTo === 'unassigned' && !lead.assigned_to) ||
      (filters.assignedTo !== 'unassigned' && lead.assigned_to === filters.assignedTo);
    
    // Date filter
    let matchesDate = true;
    if (filters.dateFrom || filters.dateTo) {
      const leadDate = new Date(lead.created_at);
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (leadDate < fromDate) matchesDate = false;
      }
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (leadDate > toDate) matchesDate = false;
      }
    }
    
    // ICP filter
    let matchesIcp = true;
    if (filters.icp === 'yes') {
      const icpScore = lead.ai_icp_match?.score || lead.icp_score?.total || 0;
      matchesIcp = icpScore >= 50;
    } else if (filters.icp === 'high') {
      const icpScore = lead.ai_icp_match?.score || lead.icp_score?.total || 0;
      matchesIcp = icpScore >= 80;
    } else if (filters.icp === 'no') {
      const icpScore = lead.ai_icp_match?.score || lead.icp_score?.total || 0;
      matchesIcp = icpScore < 50;
    }
    
    return matchesSearch && matchesStatus && matchesSource && matchesAssignedTo && matchesDate && matchesIcp;
  });

  // Iter 61 — Bronze-only filter (toggled by clicking the amber banner). Applies
  // AFTER all other filters so `bronzeOnly && search="John"` shows John leads
  // that are also Bronze. Filter resets only via the visible "Clear filter" chip.
  const finalFilteredLeads = bronzeOnly
    ? filteredLeads.filter(l => l.needs_review)
    : filteredLeads;

  // Also filter the Kanban columns when Bronze-only is active so kanban view
  // matches list view. Kanban uses `kanbanData.columns[stage].leads` directly.
  const displayedKanbanData = (bronzeOnly && kanbanData?.columns) ? {
    ...kanbanData,
    columns: Object.fromEntries(
      Object.entries(kanbanData.columns).map(([stage, col]) => [
        stage,
        { ...col, leads: (col.leads || []).filter(l => l.needs_review) }
      ])
    )
  } : kanbanData;

  const isFiltered = search.trim() || filters.status || filters.source || filters.assignedTo || filters.dateFrom || filters.dateTo || filters.icp || bronzeOnly;

  const sortedLeads = [...finalFilteredLeads].sort((a, b) => {
    if (sortBy === 'engagement_desc') return (b.engagement_score || 0) - (a.engagement_score || 0);
    if (sortBy === 'engagement_asc') return (a.engagement_score || 0) - (b.engagement_score || 0);
    if (sortBy === 'ai_score') return (b.ai_score || 0) - (a.ai_score || 0);
    if (sortBy === 'value') return (b.value || 0) - (a.value || 0);
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'created_desc') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sortBy === 'created_asc') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    if (sortBy === 'modified_desc') return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    if (sortBy === 'modified_asc') return new Date(a.updated_at || a.created_at || 0) - new Date(b.updated_at || b.created_at || 0);
    return 0;
  });

  return (
    <>
      <LeadOnboardingOverlay
        showConfetti={showConfetti}
        onConfettiComplete={handleConfettiComplete}
        highlightedLeadId={highlightedLeadId}
        onboardingStep={onboardingStep}
        highlightedCardRef={highlightedCardRef}
        onAICallAction={handleAICallAction}
        onDismissOnboarding={handleDismissOnboarding}
      />

      {/* Create/Edit Lead Sheet */}
      <LeadFormSheet
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setDuplicateError(''); }}
        editingLead={editingLead}
        formData={formData}
        setFormData={(data) => { setFormData(data); if (duplicateError) setDuplicateError(''); }}
        onSubmit={handleSubmit}
        submitting={submitting}
        pipelineStages={pipelineStages}
        leadSources={leadSources}
        teamMembers={teamMembers}
        duplicateError={duplicateError}
      />

      {/* H0 Render Contract: Loading state — view-aware skeletons with progress copy (Iter 59b) */}
      {loading && leads.length === 0 && !isDiscoveringLeads && (
        <div className="space-y-3" data-testid="leads-loading-wrapper">
          <div className="rounded-xl border border-primary/15 bg-primary/[0.03] p-3 flex items-center gap-3" data-testid="leads-loading-hint">
            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
            <p className="text-xs text-muted-foreground flex-1">
              {slowLoad
                ? 'This is taking longer than usual. Your connection or the server may be slow.'
                : `Loading your pipeline${view === 'kanban' ? ' — AI Brain is arranging columns' : ''}…`}
            </p>
            {slowLoad && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs shrink-0"
                onClick={() => { setSlowLoad(false); loadLeads(); }}
                data-testid="leads-loading-retry"
              >
                <RefreshCw className="h-3 w-3 mr-1.5" /> Retry
              </Button>
            )}
          </div>
          {view === 'kanban' ? <KanbanSkeleton /> : <TableSkeleton rows={8} />}
        </div>
      )}

      {/* Onboarding AI Discovery state — slim non-blocking banner */}
      {isDiscoveringLeads && leads.length === 0 && (
        <div className="rounded-xl border border-blue-500/20 bg-gradient-to-r from-blue-500/[0.06] to-violet-500/[0.04] p-4 mb-4 animate-[fadeUp_0.4s_ease-out]" data-testid="onboarding-discovery-state">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4 text-blue-500 animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">AI Brain is discovering prospects</p>
              <p className="text-xs text-muted-foreground">Matching your ICP and scoring leads — this usually takes 30-60 seconds</p>
            </div>
            <Loader2 className="h-4 w-4 animate-spin text-blue-500 shrink-0" />
          </div>
        </div>
      )}

      {/* Iter 59b + 61 — Bronze review banner: clickable, toggles a "Bronze only"
          filter so the user can quickly review every paused lead in one place. */}
      {!loading && leads.length > 0 && (() => {
        const reviewCount = leads.filter(l => l.needs_review).length;
        if (reviewCount === 0) return null;
        return (
          <button
            type="button"
            onClick={() => setBronzeOnly(true)}
            className="w-full text-left rounded-xl border border-amber-500/25 bg-gradient-to-r from-amber-500/[0.06] to-orange-500/[0.04] p-4 mb-4 hover:border-amber-500/50 hover:bg-amber-500/[0.10] transition-colors cursor-pointer"
            data-testid="leads-needs-review-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" data-testid="needs-review-count">
                  {reviewCount} Bronze {reviewCount === 1 ? 'lead is' : 'leads are'} awaiting your review
                </p>
                <p className="text-xs text-muted-foreground">
                  AI Brain paused outreach for these (niche ICP match or missing contact info). Click to filter, then approve or reject directly from the card.
                </p>
              </div>
              <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 shrink-0 hidden sm:inline">
                Review all →
              </span>
            </div>
          </button>
        );
      })()}

      {/* Bronze filter chip — visible when filter is active, clearable */}
      {bronzeOnly && (
        <div className="flex items-center gap-2 mb-3" data-testid="bronze-filter-active">
          <span className="text-xs text-muted-foreground">
            Filter: <span className="font-medium text-amber-600">Bronze (needs review) only</span>
          </span>
          <button
            type="button"
            onClick={() => setBronzeOnly(false)}
            className="text-xs underline text-muted-foreground hover:text-foreground"
            data-testid="clear-bronze-filter-btn"
          >
            ✕ Clear filter
          </button>
        </div>
      )}

      {/* H0 Render Contract: Error state with retry */}
      {error && leads.length === 0 && <PageError error={error} onRetry={loadLeads} backPath="/" />}

      {/* Main Content - ALWAYS show when not loading/error, or once we've loaded data at least once */}
      {!error && (hasLoadedOnceRef.current || !loading) && (
    <div className="space-y-4" data-testid="leads-page">
      <LeadToolbar
        search={search} setSearch={setSearch} setCurrentPage={setCurrentPage}
        settingsOpen={settingsOpen} setSettingsOpen={setSettingsOpen}
        pipelineStages={pipelineStages} visibleStatuses={visibleStatuses} setVisibleStatuses={setVisibleStatuses}
        importSheetOpen={importSheetOpen} setImportSheetOpen={setImportSheetOpen}
        filterOpen={filterOpen} setFilterOpen={setFilterOpen}
        filters={filters} setFilters={setFilters} sortBy={sortBy} setSortBy={setSortBy}
        leadSources={leadSources} teamMembers={teamMembers} products={products}
        view={view} setView={setView}
        hasPermission={hasPermission} resetForm={resetForm} setDialogOpen={setDialogOpen}
        discoverOpen={discoverOpen} setDiscoverOpen={setDiscoverOpen}
      />

      {/* Filter-aware CSV export (paid plans) */}
      <div className="flex justify-end -mt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportLeads}
          disabled={exporting}
          className="h-8 gap-1.5"
          data-testid="export-leads-btn"
          title="Export the current (filtered) pipeline to CSV"
        >
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Export CSV
        </Button>
      </div>

      {/* Phase 1 — Pipeline / Audience toggle (mirrors the Audience page) */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="pipeline-audience-toggle">
          <button
            className="px-4 py-1.5 text-sm font-medium bg-primary/15 text-primary border-r border-border"
            data-testid="toggle-pipeline"
          >
            Pipeline
          </button>
          <button
            onClick={() => navigate('/audience')}
            className="px-4 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
            data-testid="toggle-audience"
          >
            Audience
          </button>
        </div>
        <button
          onClick={openNotRelevant}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-amber-500/30 text-amber-600 hover:bg-amber-500/10 transition-colors"
          data-testid="not-relevant-lane-btn"
          title="Inbound callers the AI flagged as promotional / spam / wrong-number"
        >
          <ShieldAlert className="h-4 w-4" />
          Not Relevant
        </button>
        {canSeeUnassigned && (unassignedCount > 0 || filters.assignedTo === 'unassigned') && (
          <button
            onClick={() => setFilters((f) => ({ ...f, assignedTo: f.assignedTo === 'unassigned' ? '' : 'unassigned' }))}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              filters.assignedTo === 'unassigned'
                ? 'border-blue-500 bg-blue-500/15 text-blue-500'
                : 'border-border text-muted-foreground hover:bg-muted/50'
            }`}
            data-testid="unassigned-filter-chip"
            title="Show only leads that have no owner yet"
          >
            <UserX className="h-4 w-4" />
            Unassigned
            {unassignedCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500/20 text-blue-500 text-[10px] font-semibold" data-testid="unassigned-count-badge">
                {unassignedCount}
              </span>
            )}
          </button>
        )}
        <EnrollmentBadge />
        {hasPermission('lead', 'kanban', 'update') && <EmailCleanupBadge />}
        {view === 'table' && hasPermission('lead', 'kanban', 'update') && (
          <BatchSelectMenu
            onSelect={(ids, label) => {
              setSelectedLeadIds(new Set(ids));
              toast.success(`Selected ${ids.length} lead${ids.length !== 1 ? 's' : ''} · ${label} — now Move to Audience or Delete`);
            }}
          />
        )}
      </div>

      <BackfillBanner onChanged={() => loadLeadsRef.current && loadLeadsRef.current()} />

      {/* Filter indicator */}
      {isFiltered && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2" data-testid="filter-indicator">
          <Filter className="h-3.5 w-3.5" />
          <span>Showing <span className="font-medium text-foreground">{filteredLeads.length}</span> of {totalLeadCount} leads</span>
          <button
            onClick={() => { setSearch(''); setFilters({ status: '', source: '', assignedTo: '', dateFrom: '', dateTo: '', icp: '' }); setCurrentPage(1); }}
            className="text-xs text-primary hover:underline ml-1"
            data-testid="clear-all-filters"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Kanban View - Extracted to LeadKanbanView */}
      <LeadKanbanView
        view={view}
        kanbanData={displayedKanbanData}
        pipelineStages={pipelineStages}
        statusConfigs={statusConfigs}
        sortedLeads={sortedLeads}
        onDragEnd={handleDragEnd}
        handleViewDetails={handleViewDetails}
        handleRevealContact={handleRevealContact}
        loadMoreColumn={loadMoreColumn}
        loadingMoreColumn={loadingMoreColumn}
        setQuickActionLead={setQuickActionLead}
        setEmailDialogOpen={setEmailDialogOpen}
        setCallDialogOpen={setCallDialogOpen}
        openEditSheet={handleEdit}
        openLeadDetail={handleViewDetails}
        teamUsers={teamMembers}
        currency={currency}
        checkKanbanScroll={checkKanbanScroll}
        handleKanbanDragOver={handleKanbanDragOver}
        visibleStatuses={visibleStatuses}
        dragOverColumn={dragOverColumn}
        draggedLead={draggedLead}
        handleColumnDragEnter={handleColumnDragEnter}
        handleColumnDragLeave={handleColumnDragLeave}
        handleDrop={handleDrop}
        handleDragStart={handleDragStart}
        activeCallLeadId={activeCallLeadId}
        leadSummaries={leadSummaries}
        callPolling={callPolling}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        handleDragOver={handleDragOver}
        highlightedLeadId={highlightedLeadId}
        highlightedCardRef={highlightedCardRef}
        revealingLeadId={revealingLeadId}
        onLeadUpdated={(leadId, patch) => {
          // Optimistic local update so the Kanban card re-renders immediately
          // without a full re-fetch (saves 1× decrypt-all GET /api/leads).
          setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, ...patch } : l));
          // Schedule a soft refresh so the Bronze banner count updates eventually
          setTimeout(() => loadLeads(), 800);
        }}
        selectedLeadIds={selectedLeadIds}
        toggleSelectLead={toggleSelectLead}
      />
      {/* Kanban bulk-action bar — mirrors the table bulk bar so admins can multi-select
          cards and Assign / Move / Delete without switching to table view. */}
      {view === 'kanban' && selectedLeadIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-3 py-2 rounded-full border bg-background/95 backdrop-blur shadow-xl"
          data-testid="kanban-bulk-bar"
        >
          <span className="text-sm font-medium px-1" data-testid="kanban-bulk-count">
            {selectedLeadIds.size} selected
          </span>
          <div className="h-5 w-px bg-border" />
          <Button size="sm" variant="outline" className="h-8 rounded-full" data-testid="kanban-bulk-assign-btn"
            onClick={() => { setAssignSelected([]); setAssignMode('single'); setAssignOpen(true); }}>
            <UserPlus className="h-4 w-4 mr-1.5" /> Assign
          </Button>
          <Button size="sm" variant="outline" className="h-8 rounded-full" data-testid="kanban-bulk-audience-btn"
            onClick={() => setBulkMoveOpen(true)}>
            Move to Audience
          </Button>
          <Button size="sm" variant="outline" className="h-8 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10" data-testid="kanban-bulk-delete-btn"
            onClick={() => setBulkDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete
          </Button>
          <button onClick={clearSelection} className="ml-0.5 p-1 text-muted-foreground hover:text-foreground" data-testid="kanban-bulk-clear" title="Clear selection">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {/* Table View */}
      {view === 'table' && (
        <LeadTableView
          sortedLeads={sortedLeads}
          sortBy={sortBy}
          setSortBy={setSortBy}
          pipelineStages={pipelineStages}
          statusConfigs={statusConfigs}
          teamMembers={teamMembers}
          currency={currency}
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          setPageSize={setPageSize}
          hasPermission={hasPermission}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleRevealContact={handleRevealContact}
          revealingLeadId={revealingLeadId}
          selectedLeadIds={selectedLeadIds}
          toggleSelectLead={toggleSelectLead}
          toggleSelectAll={toggleSelectAll}
          clearSelection={clearSelection}
          onBulkDeleteClick={() => setBulkDeleteOpen(true)}
          onBulkMoveAudienceClick={() => setBulkMoveOpen(true)}
          onBulkAssignClick={() => { setAssignSelected([]); setAssignMode('single'); setAssignOpen(true); }}
          selectAllMatching={selectAllMatching}
          onSelectAllMatching={(v) => setSelectAllMatching(v)}
        />
      )}

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => { if (!bulkDeleting) setBulkDeleteOpen(o); }}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectAllMatching ? totalCount : selectedLeadIds.size} lead{(selectAllMatching ? totalCount : selectedLeadIds.size) !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {selectAllMatching ? `all ${totalCount} matching leads` : `the selected lead${selectedLeadIds.size !== 1 ? 's' : ''}`} along with their calls, notes, tasks, cadences and linked deals. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting} data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleBulkDelete(); }}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-confirm"
            >
              {bulkDeleting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…</>) : `Delete ${selectAllMatching ? totalCount : selectedLeadIds.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Bulk move-to-audience confirmation */}
      <AlertDialog open={bulkMoveOpen} onOpenChange={(o) => { if (!bulkMoving) setBulkMoveOpen(o); }}>
        <AlertDialogContent data-testid="bulk-move-audience-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move {selectAllMatching ? totalCount : selectedLeadIds.size} lead{(selectAllMatching ? totalCount : selectedLeadIds.size) !== 1 ? 's' : ''} to Audience?</AlertDialogTitle>
            <AlertDialogDescription>
              This takes {selectAllMatching ? `all ${totalCount} matching leads` : `the selected lead${selectedLeadIds.size !== 1 ? 's' : ''}`} off the active pipeline (Kanban) and into your cold Audience list, and pauses any active outreach so the AI stops contacting them. Nothing is deleted — you can promote them back anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkMoving} data-testid="bulk-move-audience-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleBulkMoveAudience(); }}
              disabled={bulkMoving}
              data-testid="bulk-move-audience-confirm"
            >
              {bulkMoving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Moving…</>) : `Move ${selectAllMatching ? totalCount : selectedLeadIds.size} to Audience`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk assign dialog */}
      <Dialog open={assignOpen} onOpenChange={(o) => { if (!assigning) setAssignOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign {selectAllMatching ? totalCount : selectedLeadIds.size} lead{(selectAllMatching ? totalCount : selectedLeadIds.size) !== 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Pick teammate{assignMode === 'round_robin' ? 's' : ''}</p>
              <div className="max-h-56 overflow-y-auto rounded-md border divide-y" data-testid="assign-teammate-list">
                {(teamMembers || []).length === 0 && (
                  <div className="px-3 py-4 text-sm text-muted-foreground" data-testid="assign-no-teammates">
                    No teammates yet — invite team members first from the Team page.
                  </div>
                )}
                {(teamMembers || []).map((m) => {
                  const checked = assignSelected.includes(m.id);
                  return (
                    <button
                      type="button"
                      key={m.id}
                      data-testid={`assign-teammate-${m.id}`}
                      onClick={() => setAssignSelected((prev) => {
                        if (assignMode === 'single') return [m.id];
                        return prev.includes(m.id) ? prev.filter((x) => x !== m.id) : [...prev, m.id];
                      })}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${checked ? 'bg-primary/10' : 'hover:bg-muted/60'}`}
                    >
                      <span>
                        <span className="font-medium">{m.name || m.email}</span>
                        {m.role && <span className="ml-2 text-xs text-muted-foreground">{m.role}</span>}
                      </span>
                      {checked && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">How to distribute</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={assignMode === 'single' ? 'default' : 'outline'}
                  size="sm"
                  data-testid="assign-mode-single"
                  onClick={() => { setAssignMode('single'); setAssignSelected((p) => p.slice(0, 1)); }}
                >
                  All to one teammate
                </Button>
                <Button
                  type="button"
                  variant={assignMode === 'round_robin' ? 'default' : 'outline'}
                  size="sm"
                  data-testid="assign-mode-round-robin"
                  onClick={() => setAssignMode('round_robin')}
                >
                  Split evenly (round-robin)
                </Button>
              </div>
              {assignMode === 'round_robin' && (
                <p className="text-xs text-muted-foreground mt-2">Leads will be divided evenly across the selected teammates.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} disabled={assigning} data-testid="assign-cancel">Cancel</Button>
            <Button onClick={handleBulkAssignConfirm} disabled={assigning || assignSelected.length === 0} data-testid="assign-confirm">
              {assigning ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Assigning…</>) : `Assign ${selectAllMatching ? totalCount : selectedLeadIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Not Relevant review lane — inbound callers the AI quarantined */}
      <Dialog open={notRelevantOpen} onOpenChange={setNotRelevantOpen}>
        <DialogContent className="max-w-2xl" data-testid="not-relevant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Not Relevant — Inbound Review Lane
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            The AI flagged these inbound callers as promotional, spam, or wrong-number and kept them off your pipeline. Review and restore any it got wrong, or clear them out.
          </p>
          {notRelevantLeads.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-y py-2 mt-1" data-testid="nr-action-bar">
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" data-testid="nr-select-all"
                  checked={nrSelected.size === notRelevantLeads.length && notRelevantLeads.length > 0}
                  onChange={(e) => setNrSelected(e.target.checked ? new Set(notRelevantLeads.map((l) => l.id)) : new Set())} />
                {nrSelected.size > 0 ? `${nrSelected.size} selected` : 'Select all'}
              </label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="text-red-600 border-red-500/30" disabled={nrSelected.size === 0 || nrClearing}
                  onClick={() => handleBulkClear(false)} data-testid="nr-clear-selected-btn">
                  {nrClearing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                  Clear selected
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" disabled={nrClearing}
                  onClick={() => handleBulkClear(true)} data-testid="nr-clear-all-btn">
                  Clear all
                </Button>
              </div>
            </div>
          )}
          <div className="space-y-2 max-h-[60vh] overflow-y-auto mt-2">
            {nrLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : notRelevantLeads.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm" data-testid="not-relevant-empty">
                Nothing quarantined — every inbound caller looked genuine. 🎉
              </div>
            ) : (
              notRelevantLeads.map((l) => (
                <div key={l.id} className="flex items-start gap-3 rounded-lg border p-3" data-testid={`not-relevant-row-${l.id}`}>
                  <input type="checkbox" className="mt-1" checked={nrSelected.has(l.id)}
                    onChange={() => toggleNrSelect(l.id)} data-testid={`nr-select-${l.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <PhoneIncoming className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="font-medium truncate">{l.name || l.phone || 'Unknown caller'}</span>
                      {l.inbound_intent && (
                        <Badge variant="outline" className="text-[10px] bg-amber-500/5 border-amber-500/30 text-amber-600 capitalize">
                          {String(l.inbound_intent).replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                    {l.inbound_summary && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.inbound_summary}</p>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleRestoreRelevant(l)} data-testid={`restore-relevant-${l.id}`}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Restore
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>



      {/* Lead Preview Offcanvas */}
      <LeadPreviewPanel
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        lead={previewLead}
        statusConfigs={statusConfigs}
        currency={currency}
        hasPermission={hasPermission}
        onViewDetails={handleViewDetails}
        onEmailClick={handleOpenEmailDialog}
        onCallClick={handleOpenCallDialog}
        onAICallClick={handleInitiateAICall}
        onNoteClick={handleOpenNoteDialog}
        onEdit={handleEdit}
        aiStatus={aiStatus}
        aiActivities={aiActivities}
        aiLoading={aiLoading}
        onRunSalesAI={handleRunSalesAI}
      />

      {/* Quick Action Dialogs */}
      <EmailDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}
        leadName={quickActionLead?.name} form={emailForm} setForm={setEmailForm} onSend={handleSendEmail} />
      <NoteDialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}
        leadName={quickActionLead?.name} form={noteForm} setForm={setNoteForm} onSave={handleSaveNote} />
      <CallDialog open={callDialogOpen} onOpenChange={setCallDialogOpen}
        leadName={quickActionLead?.name} form={callForm} setForm={setCallForm} onSave={handleSaveCall} />
      <SummaryDialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen} data={summaryDialogData} />
    </div>
      )}

      {/* Import Sheet */}
      <LeadImportSheet
        open={importSheetOpen}
        onOpenChange={setImportSheetOpen}
        importStatus={importStatus}
        importFile={importFile}
        importData={importData}
        importProgress={importProgress}
        importResult={importResult}
        isDraggingFile={isDraggingFile}
        duplicateCheck={duplicateCheck}
        duplicateStrategy={duplicateStrategy}
        setDuplicateStrategy={setDuplicateStrategy}
        skipAiScoring={skipAiScoring}
        setSkipAiScoring={setSkipAiScoring}
        checkingDuplicates={checkingDuplicates}
        duplicateCheckFailed={duplicateCheckFailed}
        fileInputRef={fileInputRef}
        onFileDrop={handleFileDrop}
        onFileDragOver={handleFileDragOver}
        onFileDragLeave={handleFileDragLeave}
        onFileInputChange={handleFileInputChange}
        onDownloadSample={downloadSampleTemplate}
        onImport={handleImportLeads}
        onReset={resetImport}
        onTopUp={() => { setImportSheetOpen(false); resetImport(); navigate('/billing'); }}
      />

      {/* Discover Leads Dialog */}
      <LeadDiscoverDialog
        discoverOpen={discoverOpen}
        setDiscoverOpen={setDiscoverOpen}
        discovering={discovering}
        discoveredContacts={discoveredContacts}
        selectedDiscovered={selectedDiscovered}
        importing={importing}
        handleDiscoverLeads={handleDiscoverLeads}
        handleImportSelected={handleImportSelected}
        selectAllDiscovered={selectAllDiscovered}
        toggleDiscoveredSelection={toggleDiscoveredSelection}
      />

      {/* Bulk Reveal Confirmation Dialog */}
      <BulkRevealDialog
        bulkRevealConfirm={bulkRevealConfirm}
        setBulkRevealConfirm={setBulkRevealConfirm}
        confirmBulkReveal={confirmBulkReveal}
      />

      {/* Bulk ICP Reveal Confirmation Dialog */}
      <BulkIcpRevealDialog
        bulkIcpRevealOpen={bulkIcpRevealOpen}
        setBulkIcpRevealOpen={setBulkIcpRevealOpen}
        bulkIcpRevealing={bulkIcpRevealing}
        confirmBulkIcpReveal={confirmBulkIcpReveal}
      />
    </>
  );
}
